// Product-page "Request a Quote" button — AUTO-PLACE via the app embed.
//
// The cooperative placement path is the QuoteSense app block dropped
// next to Add-to-Cart in the theme editor. This module is the
// UNIVERSAL fallback for LOCKED / component-based themes (e.g. The
// Sensory Supply: ATC + dynamic-checkout + wishlist bundled inside one
// self-contained block that can't nest an app block) — it injects the
// button with plain DOM, driven entirely by shop_config.product_button.
//
// Design mirrors catalog-rules.ts: pure, DOM-free helpers (unit-tested
// in a node-only vitest without jsdom) + a thin DOM-side activator that
// isn't unit-tested (end-to-end coverage is the cross-theme manual test
// on staging). The fragile parts — anchor resolution, click-time variant
// read, idempotency, re-assert-on-mutation, never-throw — all live in
// small testable functions.
//
// Robustness contract (this ships to every merchant storefront when a
// merchant opts in, so it must degrade gracefully, never throw):
//   - Gate: no-op unless product_button.auto_place_enabled === true.
//   - Multi-form pages: findProductForm scores EVERY /cart/add form on
//     the page (visible + carries the variant selector + largest wins)
//     instead of blindly taking the first, so a theme with a hidden
//     quick-buy form or a bundled recommendation form still lands the
//     default anchor chain on the real product form.
//   - Anchor chain: [data-add-to-cart] → button[name="add"] →
//     [name="add"] → .product-form__submit →
//     button[type="submit"][name="add"] → [type="submit"] (last
//     resort), with a custom target_selector escape hatch and an
//     end-of-form append fallback.
//   - Styling parity with the app block (button_style/shape_mode/
//     font_mode/width): style_mode "color" (default) is today's
//     bg/text-color pill with shape + font axes; "outlined" is its
//     inverted twin (transparent bg, colored border/text);
//     "theme_button" clones the ATC anchor's own CSS classes and drops
//     all inline chrome so the theme's native button styling applies;
//     "theme_inverted" clones the same classes but inline-overrides
//     color to the anchor's own color flipped filled<->outlined
//     (Phase 1.4, degrades to theme_button on unreadable colors);
//     "custom_class" does the same chrome-drop as theme_button with a
//     merchant-pasted class string. "color"/"outlined" now also copy
//     the anchor's own computed padding/min-height/font-size/line-
//     height (Phase 1.4) so they match the theme button's dimensions
//     instead of rendering thinner. Unset config renders the same as
//     the pre-Phase-1.2 hardcoded pill, plus a vertical margin (Phase
//     1.3 spacing fix — theme_button/theme_inverted copy the anchor's
//     own computed margin, every other mode gets a sensible default) so
//     the button never sits flush against a neighbouring element.
//   - Variant is read AT CLICK TIME ([name="id"]) — component themes
//     swap variants via JS, not a Shopify section re-render.
//   - Idempotent: the dedup guard is DOCUMENT-WIDE (one auto-place
//     button per page), not scoped to a single form — a custom
//     target_selector can resolve into a DIFFERENT /cart/add form than
//     the one initially picked, and a form-scoped guard would miss
//     that and re-inject forever.
//   - MutationObserver re-asserts after theme re-renders / async
//     wishlist-app (Swym) injection / variant swaps — no duplicates.
//
// Phase-2 polish (sibling-shape matching, replace-existing selector,
// per-product exclude_tags) is intentionally OUT of scope.

import { readApoSelections } from "./apo-adapter";

// ── Config resolution (pure) ──────────────────────────────────────

/** Mirror of ProductButtonConfig in src/lib/shop-config-types.ts. The
 *  embed bundle can't import @/lib (would pull pg/redis into the
 *  bundle), so the shape is duplicated here — keep them in sync. */
export interface ProductButtonConfig {
  auto_place_enabled?: boolean;
  label?: string;
  bg_color?: string;
  text_color?: string;
  /** Click behavior. "form" (default) opens the request form seeded
   *  with this product — today's behavior, unchanged. "add_to_quote"
   *  adds the live-selected variant (+ any line-item properties) to the
   *  standalone quote basket and shows a brief confirmation, WITHOUT
   *  opening the form. Gated further at the call site on basket.enabled
   *  (see index.ts) so a merchant can't half-enable ATQ. */
  mode?: "form" | "add_to_quote";
  /** Which form the button opens (docs/MULTI_FORM.md §6). Unset/blank =
   *  the shop's default form. Only meaningful when mode === "form". */
  form_id?: string;
  open_mode?: "quick" | "full";
  display_mode?: "drawer" | "modal";
  target_selector?: string;
  position?: "after" | "before";
  /** Styling axis — mirrors the app block's `button_style`. "color"
   *  (default) uses bg_color/text_color + shape/font. "outlined" is the
   *  inverted twin — transparent background, bg_color as the border +
   *  text color instead — same shape/font axes. "theme_button" clones
   *  the anchor's own CSS classes onto the button and drops all inline
   *  chrome so the theme's native button styling applies. "theme_inverted"
   *  also clones the anchor's classes (same size/shape/font) but
   *  inline-overrides the colors to the anchor's own colors, flipped
   *  filled↔outlined — degrades to "theme_button" when the anchor's
   *  colors can't be read. "custom_class" applies button_class instead
   *  of cloning. */
  style_mode?: "color" | "outlined" | "theme_button" | "theme_inverted" | "custom_class";
  /** Space-separated theme CSS class(es), used when style_mode is
   *  "custom_class". */
  button_class?: string;
  /** "auto" (default) = inline-block, content width. "full" = block,
   *  100% width. */
  width?: "full" | "auto";
  /** "rounded" (default) reproduces the original hardcoded pill.
   *  "inherit" drops border-radius + padding for theme_button/
   *  custom_class's own CSS to control both. */
  shape?: "rounded" | "pill" | "square" | "inherit";
  /** "inherit" (default) reproduces today's exact font declarations —
   *  back-compat. "system" swaps in an explicit system stack. Dropped
   *  entirely under style_mode "theme_button" / "custom_class". */
  font_mode?: "inherit" | "system";
}

/** Every field defaulted — the injector reads only this. */
export interface ResolvedProductButton {
  enabled: boolean;
  label: string;
  bgColor: string;
  textColor: string;
  /** "form" (default) opens the request form; "add_to_quote" adds to
   *  the basket. */
  mode: "form" | "add_to_quote";
  /** "" when unset — the request form opens the shop's default form. */
  formId: string;
  openMode: "quick" | "full";
  displayMode: "drawer" | "modal";
  /** "" when no custom selector configured. */
  targetSelector: string;
  position: "after" | "before";
  styleMode: "color" | "outlined" | "theme_button" | "theme_inverted" | "custom_class";
  /** "" when style_mode isn't "custom_class" or unset. */
  buttonClass: string;
  width: "full" | "auto";
  shape: "rounded" | "pill" | "square" | "inherit";
  fontMode: "inherit" | "system";
}

/** Opts handed to window.QuoteApp.openRequest() at click time. */
export interface OpenRequestOpts {
  productId?: string;
  variantId?: string;
  formMode: "quick" | "full";
  displayMode: "drawer" | "modal";
  /** Which form to load (docs/MULTI_FORM.md §6). Omitted → the shop's
   *  default form — mirrors window.QuoteApp.openRequest's own `formId`. */
  formId?: string;
  /** Merged line-item properties for this click — theme form inputs
   *  (readLineItemProperties) plus any synthesized APO v2 selections
   *  (readApoSelections), form inputs winning on name collisions.
   *  Absent when neither source has anything (today's byte-identical
   *  no-APO case). */
  properties?: LineItemProperty[];
}

export const AUTO_PLACE_BTN_CLASS = "qs-autoplace-btn";
export const AUTO_PLACE_BTN_ATTR = "data-qs-autoplace";
/** Identity attribute on the injected cascade-authority `<style>`
 *  element (Lane 3 / B2) — see injectAutoPlaceStyleTag. */
export const AUTO_PLACE_STYLE_ATTR = "data-qs-autoplace-style";
export const PRODUCT_FORM_SELECTOR = 'form[action*="/cart/add"]';
/** Anchor precedence within the product form (first hit wins). Prefers
 *  the real add-to-cart control before falling back to a loose
 *  `[type="submit"]`, which can match a variant-swatch/other submit
 *  that sits above the ATC on some themes. */
export const ANCHOR_CHAIN: readonly string[] = [
  "[data-add-to-cart]",
  'button[name="add"]',
  '[name="add"]',
  ".product-form__submit",
  'button[type="submit"][name="add"]',
  '[type="submit"]',
];
export const VARIANT_SELECTOR = '[name="id"]';
export const PRODUCT_ID_SELECTOR = '[name="product-id"]';

/** The Shopify dynamic-checkout / payment-button cluster — Shop Pay,
 *  Apple Pay, Google Pay, PayPal express buttons. Shared with
 *  catalog-rules.ts's Hide-ATC selector list (:113-115) so "never
 *  auto-place our button here" and "always hide these when Hide ATC is
 *  on" stay in lockstep instead of silently drifting apart. Never a
 *  valid anchor even when visible — placing beside/cloning one of
 *  these would put our button in the checkout-bypass cluster the
 *  merchant is trying to route around. */
export const PAYMENT_CLUSTER_SELECTORS: readonly string[] = [
  ".shopify-payment-button",
  "shopify-payment-button",
  '[class*="dynamic-checkout"]',
  ".additional-checkout-buttons",
];

/** Option-app clone markers to try AFTER the specific chain selectors
 *  and BEFORE the bare `[type="submit"]` last resort (A1-A6 root
 *  cause: MageWorx APO v2 and similar apps hide the real ATC and
 *  render a `type="button"` clone that the specific selectors miss,
 *  so the last resort was landing on the hidden original). */
const CLONE_MARKER_SELECTOR = ".mw-apo-add-to-cart-cloned";
/** Matches "Add to Cart" (any case, internal whitespace) as a whole
 *  phrase — used to recognize an option-app's ATC clone by its visible
 *  text when it carries no recognizable class/attribute marker. */
const CLONE_TEXT_MATCH = /\badd to cart\b/i;

const DEFAULT_LABEL = "Request a quote";
const DEFAULT_TEXT_COLOR = "#ffffff";
/** Last-resort background when neither bg_color nor accent is set —
 *  matches DEFAULT_SHOP_CONFIG.accent_color so the button never
 *  renders unstyled/transparent. */
const FALLBACK_BG = "#2563eb";

function trimStr(v: unknown): string {
  return typeof v === "string" ? v.trim() : "";
}

export function resolveProductButtonConfig(
  pb: ProductButtonConfig | null | undefined,
  accentColor?: string | null,
): ResolvedProductButton {
  const c = pb || {};
  return {
    enabled: c.auto_place_enabled === true,
    label: trimStr(c.label) || DEFAULT_LABEL,
    bgColor: trimStr(c.bg_color) || trimStr(accentColor) || FALLBACK_BG,
    textColor: trimStr(c.text_color) || DEFAULT_TEXT_COLOR,
    // Default "form" preserves today's behavior for every existing
    // merchant; ATQ is strictly opt-in.
    mode: c.mode === "add_to_quote" ? "add_to_quote" : "form",
    formId: trimStr(c.form_id),
    // Default open mode is "quick" (single-screen, prefilled).
    openMode: c.open_mode === "full" ? "full" : "quick",
    displayMode: c.display_mode === "modal" ? "modal" : "drawer",
    targetSelector: trimStr(c.target_selector),
    position: c.position === "before" ? "before" : "after",
    // Style axes — every default below reproduces today's hardcoded
    // pill exactly (see applyButtonStyle's back-compat note).
    styleMode:
      c.style_mode === "theme_button" || c.style_mode === "theme_inverted" ||
      c.style_mode === "custom_class" || c.style_mode === "outlined"
        ? c.style_mode
        : "color",
    buttonClass: trimStr(c.button_class),
    width: c.width === "full" ? "full" : "auto",
    shape: c.shape === "pill" || c.shape === "square" || c.shape === "inherit" ? c.shape : "rounded",
    fontMode: c.font_mode === "system" ? "system" : "inherit",
  };
}

// ── DOM contracts (structural — real Elements/Documents satisfy them,
//    and node-only tests can supply lightweight fakes) ──────────────

interface ValueEl {
  value?: string | null;
}
interface QueryableLike {
  querySelector(selectors: string): ElementLike | null;
}
interface ElementLike extends QueryableLike, ValueEl {}
interface DocumentLike extends QueryableLike {
  createElement(tag: string): any;
}

// ── Anchor resolution (pure) ──────────────────────────────────────

/** True when a candidate form is hidden — disqualifies it outright so
 *  a hidden-but-larger form never outranks a visible smaller one.
 *  offsetParent is a real-DOM-only signal (undefined in node-only
 *  tests / older structural doubles) so it's skipped rather than
 *  treated as "hidden" when unavailable. */
function isHiddenForm(form: any): boolean {
  try {
    if (form.hasAttribute && form.hasAttribute("hidden")) return true;
    const style = form.style;
    if (style && (style.display === "none" || style.visibility === "hidden")) return true;
    if (typeof form.offsetParent !== "undefined" && form.offsetParent === null) return true;
  } catch {
    /* ignore — never let a hostile form crash detection */
  }
  return false;
}

/** Score a candidate `/cart/add` form for "is this the visible/main
 *  product form" — used when a theme has more than one cart form on
 *  one page (e.g. a bundled recommendation widget alongside the real
 *  product form, or a hidden quick-buy form injected earlier in the
 *  DOM). Hidden forms are disqualified; carrying the variant selector
 *  is the dominant signal for "main"; descendant count is the
 *  "largest" tiebreaker. */
function scoreProductForm(form: any): number {
  if (isHiddenForm(form)) return -1;
  let score = 0;
  try {
    if (form.querySelector && form.querySelector(VARIANT_SELECTOR)) score += 1000;
  } catch {
    /* ignore */
  }
  try {
    score += typeof form.querySelectorAll === "function" ? form.querySelectorAll("*").length : 0;
  } catch {
    /* ignore */
  }
  return score;
}

/** Find the product form on the page. With a single `/cart/add` form
 *  this is just that form (unchanged single-form behavior). With
 *  MULTIPLE forms — the demo-store edge, e.g. a bundled recommendation
 *  widget or a hidden quick-buy form sharing the page with the real
 *  product form — picks the best-scoring one (see scoreProductForm)
 *  instead of blindly taking the first, so multi-form themes land on
 *  the real Add-to-Cart without a custom Target selector. Returns null
 *  (not a product page / unsupported theme) rather than throwing on a
 *  bad root. */
export function findProductForm(root: QueryableLike | null | undefined): ElementLike | null {
  if (!root) return null;
  try {
    const anyRoot: any = root;
    if (typeof anyRoot.querySelectorAll !== "function") {
      // Structural fallback for callers that only implement
      // querySelector (e.g. a single-element ElementLike scope).
      return root.querySelector(PRODUCT_FORM_SELECTOR);
    }
    const forms: any[] = Array.from(anyRoot.querySelectorAll(PRODUCT_FORM_SELECTOR));
    if (forms.length === 0) return null;
    if (forms.length === 1) return forms[0];
    let best = forms[0];
    let bestScore = scoreProductForm(forms[0]);
    for (let i = 1; i < forms.length; i++) {
      const score = scoreProductForm(forms[i]);
      if (score > bestScore) {
        best = forms[i];
        bestScore = score;
      }
    }
    return best;
  } catch {
    return null;
  }
}

/** Resolve `getComputedStyle`, preferring an injected `win` (tests)
 *  over the ambient global (real browsers / jsdom). Mirrors the
 *  fallback pattern computeAnchorSize/computeAnchorMargin already use. */
function resolveGetComputedStyle(win?: any): ((el: any) => any) | null {
  return (
    (win && typeof win.getComputedStyle === "function" && win.getComputedStyle) ||
    (typeof getComputedStyle === "function" ? getComputedStyle : null)
  );
}

/** True when a candidate anchor is actually visible — the A1-A6 root
 *  fix. jsdom has no layout engine: getClientRects()/offsetParent are
 *  always empty/null for EVERY node there, real or hidden (see the
 *  jsdom test file's comments), so they can't be the primary signal
 *  the way a naive "check the browser" implementation would use them.
 *  getComputedStyle's display/visibility resolves correctly in both
 *  real browsers AND jsdom (cascade + inline + the `hidden` attribute
 *  all included), so it's the PRIMARY check; a computed 0x0 size (some
 *  clone-and-hide implementations zero the box instead of, or in
 *  addition to, display:none — the MageWorx case does both) is the
 *  secondary check. getClientRects/offsetParent are the fallback for
 *  when no getComputedStyle is reachable at all (legacy/hostile host).
 *  A structural test double with NONE of these (the node-only
 *  product-button.test.ts fakes) is treated as visible — first-match
 *  wins, exactly like before this lane, so the existing suite doesn't
 *  need a fake DOM rewrite. Never throws. */
function isVisibleAnchor(el: any, win?: any): boolean {
  if (!el) return false;
  try {
    if (typeof el.hasAttribute === "function" && el.hasAttribute("hidden")) return false;

    const getCS = resolveGetComputedStyle(win);
    if (getCS) {
      try {
        const cs = getCS(el);
        if (cs) {
          if (cs.display === "none" || cs.visibility === "hidden") return false;
          if (cs.width === "0px" && cs.height === "0px") return false;
          return true;
        }
      } catch {
        /* hostile CSSOM — fall through to the geometry fallback */
      }
    }

    if (typeof el.getClientRects === "function") {
      const rects = el.getClientRects();
      if (rects && typeof rects.length === "number") {
        if (rects.length === 0) return false;
        for (let i = 0; i < rects.length; i++) {
          const r = rects[i];
          if (r && ((r.width || 0) > 0 || (r.height || 0) > 0)) return true;
        }
        return false;
      }
    }
    if (typeof el.offsetParent !== "undefined") return el.offsetParent !== null;

    return true; // structural double — no rendering signal available at all.
  } catch {
    return true; // never let a hostile node block resolution
  }
}

/** True when `el` is inside/matches the payment-cluster exclusion list
 *  (PAYMENT_CLUSTER_SELECTORS) — never a valid anchor even when
 *  visible. `closest` covers both "matches" and "is a descendant of" a
 *  cluster element in one walk. */
function isExcludedAnchor(el: any): boolean {
  if (!el) return true;
  for (const sel of PAYMENT_CLUSTER_SELECTORS) {
    try {
      if (typeof el.closest === "function" && el.closest(sel)) return true;
    } catch {
      /* malformed/unsupported selector on this engine — skip */
    }
  }
  return false;
}

/** Run `sel` against `scope` (a form or the whole document) and return
 *  the first VISIBLE, non-excluded match — the single primitive both
 *  the chain walk and target_selector resolution build on. Falls back
 *  to a single querySelector probe for scopes that only implement that
 *  (structural ElementLike doubles). Never throws — a malformed
 *  selector on this engine is treated as "no match", not an error. */
function firstVisibleMatch(scope: any, sel: string, win?: any): any {
  try {
    const candidates: any[] =
      typeof scope.querySelectorAll === "function"
        ? Array.from(scope.querySelectorAll(sel))
        : (() => {
            try {
              const one = scope.querySelector ? scope.querySelector(sel) : null;
              return one ? [one] : [];
            } catch {
              return [];
            }
          })();
    for (const el of candidates) {
      if (isExcludedAnchor(el)) continue;
      if (isVisibleAnchor(el, win)) return el;
    }
  } catch {
    /* malformed selector — treat as no match */
  }
  return null;
}

/** Collapse internal whitespace in an element's visible text — used to
 *  compare against CLONE_TEXT_MATCH regardless of how a theme/app
 *  formats the label (line breaks, double spaces from nested spans). */
function normalizeAnchorText(s: unknown): string {
  return String(s == null ? "" : s)
    .replace(/\s+/g, " ")
    .trim();
}

/** Clone-aware fallback (A1-A6): tried AFTER the specific chain
 *  selectors and BEFORE the bare `[type="submit"]` last resort. Step 1
 *  is the MageWorx APO v2 clone marker class; step 2 is any visible
 *  button/role=button in the form whose normalized text reads "add to
 *  cart" — covers other option apps that clone the ATC as a plain
 *  `type="button"` control with no recognizable class/attribute.
 *  Queried as three separate selectors (not one comma-list) so the
 *  node-only structural test doubles — whose tiny selector parser
 *  doesn't support combinators — can exercise this path too. */
function findCloneAwareAnchor(form: any, win?: any): any {
  const cloneMarkerHit = firstVisibleMatch(form, CLONE_MARKER_SELECTOR, win);
  if (cloneMarkerHit) return cloneMarkerHit;

  try {
    if (typeof form.querySelectorAll !== "function") return null;
    const candidates: any[] = [
      ...Array.from(form.querySelectorAll("button") as any),
      ...Array.from(form.querySelectorAll('a[role="button"]') as any),
      ...Array.from(form.querySelectorAll('[role="button"]') as any),
    ];
    const seen = new Set<any>();
    for (const el of candidates) {
      if (seen.has(el)) continue;
      seen.add(el);
      if (isExcludedAnchor(el)) continue;
      if (!isVisibleAnchor(el, win)) continue;
      if (CLONE_TEXT_MATCH.test(normalizeAnchorText(el.textContent))) return el;
    }
  } catch {
    /* ignore — never let a hostile form crash resolution */
  }
  return null;
}

/** Resolve the injection anchor. A custom target_selector is queried
 *  against BOTH the form and the whole document — a match INSIDE the
 *  product form wins (a sticky/duplicate bar elsewhere on the page
 *  must not shadow the real in-form control), else the first VISIBLE
 *  document-wide match; matches that are ALL hidden count as no match
 *  (the caller falls back to appending at the end of the form) rather
 *  than silently anchoring on something invisible. Without a
 *  target_selector, the built-in add-to-cart chain is tried within the
 *  form: the specific selectors first, then the clone-aware fallback
 *  (findCloneAwareAnchor), then the bare `[type="submit"]` last
 *  resort — every step visibility-gated and exclusion-filtered (never
 *  the payment/dynamic-checkout cluster, see PAYMENT_CLUSTER_SELECTORS).
 *  Returns null when nothing matches. */
export function resolveAnchor(opts: {
  form: QueryableLike;
  doc?: QueryableLike | null;
  targetSelector?: string;
  win?: any;
}): ElementLike | null {
  const { form, doc, targetSelector, win } = opts;
  if (targetSelector) {
    try {
      const anyDoc: any = doc;
      if (doc && typeof anyDoc.querySelectorAll === "function") {
        const inForm = firstVisibleMatch(form, targetSelector, win);
        if (inForm) return inForm;
        return firstVisibleMatch(doc, targetSelector, win);
      }
      // Structural fallback — doc only implements querySelector, no
      // visibility/exclusion filtering possible.
      return (doc && anyDoc.querySelector && anyDoc.querySelector(targetSelector)) || null;
    } catch {
      return null;
    }
  }

  const specificSelectors = ANCHOR_CHAIN.slice(0, -1);
  const lastResort = ANCHOR_CHAIN[ANCHOR_CHAIN.length - 1];

  for (const sel of specificSelectors) {
    const hit = firstVisibleMatch(form, sel, win);
    if (hit) return hit;
  }

  const cloneHit = findCloneAwareAnchor(form, win);
  if (cloneHit) return cloneHit;

  return firstVisibleMatch(form, lastResort, win);
}

export interface Placement {
  anchor: ElementLike | null;
  mode: "before" | "after" | "append";
}

/** Combine anchor resolution + configured position into a single
 *  placement decision. No anchor → append at end of form. */
export function resolvePlacement(opts: {
  form: QueryableLike;
  doc?: QueryableLike | null;
  targetSelector?: string;
  position?: "after" | "before";
  win?: any;
}): Placement {
  const anchor = resolveAnchor(opts);
  if (!anchor) return { anchor: null, mode: "append" };
  return { anchor, mode: opts.position === "before" ? "before" : "after" };
}

/** True when the page already carries an auto-placed button. The
 *  idempotency guard — the re-assert path re-runs the injector and
 *  relies on this to avoid duplicates. Keys off AUTO_PLACE_BTN_ATTR
 *  (data-qs-autoplace) rather than the CSS class (B3): the class is a
 *  styling hook a theme_button clone can end up sharing with a
 *  merchant's own markup, where the attribute is ours alone. The class
 *  is still applied for styling/back-compat — only identity checks
 *  moved. */
export function hasAutoPlaceButton(root: QueryableLike | null | undefined): boolean {
  if (!root) return false;
  try {
    return !!root.querySelector("[" + AUTO_PLACE_BTN_ATTR + "]");
  } catch {
    return false;
  }
}

// ── Cart-icon anchor resolution (pure) ────────────────────────────
// The Quote Basket's cart-adjacent "Quote (N)" count needs to find the
// theme's header cart icon/link so it can sit beside it (no floating
// bubble). There is no single cross-theme contract for that element, so
// — mirroring ANCHOR_CHAIN for the product form — we try a precedence
// chain of the selectors common themes use, header-specific ids/classes
// first (least likely to false-match a footer/other cart link), then
// the broad href-based and aria-label fallbacks. First hit wins; a null
// return means "no detectable cart icon" and the caller renders nothing.
export const CART_ANCHOR_CHAIN: readonly string[] = [
  "#cart-icon-bubble",        // Dawn + Shopify reference themes
  ".cart-count-bubble",       // Dawn count node
  "[data-cart-icon]",
  ".site-header__cart",       // Brooklyn / older OS themes
  ".header__icon--cart",      // Debut-family
  'a[href$="/cart"]',         // generic cart link (exact)
  'a[href*="/cart"]',         // generic cart link (loose — query params etc.)
  '[aria-label*="cart" i]',   // last resort — anything labelled "cart"
];

/** Find the theme's header cart icon/link to anchor the cart-adjacent
 *  "Quote (N)" count next to. Queried against the whole document (the
 *  cart icon lives in the header, outside any product form). Returns the
 *  first CART_ANCHOR_CHAIN match, or null when the theme exposes no
 *  recognizable cart element — in which case the caller renders no count
 *  (re-entry is covered by the add toast's "Review quote →" link). */
export function resolveCartAnchor(doc: QueryableLike | null | undefined): ElementLike | null {
  if (!doc || typeof doc.querySelector !== "function") return null;
  for (const sel of CART_ANCHOR_CHAIN) {
    try {
      const el = doc.querySelector(sel);
      if (el) return el;
    } catch {
      /* malformed/unsupported selector on this engine — skip */
    }
  }
  return null;
}

// ── Click payload (pure) ──────────────────────────────────────────

/** Build the openRequest opts from the LIVE form state at click time.
 *  Variant: [name="id"] (kept in sync by the theme's variant picker)
 *  → any <select> fallback. Product: [name="product-id"] → the page
 *  product context passed in. formMode maps open_mode → the value the
 *  embed's openRequest() understands; "full" is passed explicitly (not
 *  left undefined) so the resolved mode is deterministic regardless of
 *  display_mode or any script-tag data-form-mode.
 *
 *  De-dupe with index.ts's core capture (option-capture.ts): this is
 *  the ONE place auto-place's own click captures properties, scoped to
 *  the exact form the click resolved to (matters on multi-form pages —
 *  a page-wide re-scan can't guarantee the same form). The `properties`
 *  this produces flows straight through to api.openRequest() as
 *  opts.properties, which the core treats as already-resolved and never
 *  re-captures — see resolveOpenRequestProperties in index.ts. Every
 *  OTHER door (app block, manual `openRequest()` snippets) never builds
 *  its own properties here, so the core's capture is what covers them. */
export function buildOpenRequestOpts(opts: {
  form: QueryableLike;
  openMode: "quick" | "full";
  displayMode: "drawer" | "modal";
  fallbackProductId?: string;
  formId?: string;
  /** Storefront `window` — probed for APO v2 selections (readApoSelections).
   *  Absent (e.g. existing callers/tests that don't pass it) → no APO
   *  properties, byte-identical to before this adapter existed. */
  win?: any;
  /** Storefront `document` — threaded through to readApoSelections for
   *  parity with its signature; unused today (see apo-adapter.ts). */
  doc?: any;
}): OpenRequestOpts {
  const { form, openMode, displayMode, fallbackProductId, formId, win, doc } = opts;
  const readVal = (sel: string): string => {
    try {
      const el = form.querySelector(sel);
      return el && el.value != null ? String(el.value).trim() : "";
    } catch {
      return "";
    }
  };
  const variantId = readVal(VARIANT_SELECTOR) || readVal("select");
  const productId = readVal(PRODUCT_ID_SELECTOR) || trimStr(fallbackProductId);
  const out: OpenRequestOpts = {
    formMode: openMode === "quick" ? "quick" : "full",
    displayMode,
  };
  if (variantId) out.variantId = variantId;
  if (productId) out.productId = productId;
  if (formId) out.formId = formId;
  const properties = mergeLineItemProperties(readLineItemProperties(form as unknown as MultiQueryableLike), readApoSelections(win, doc));
  if (properties.length) out.properties = properties;
  return out;
}

// ── Add-to-Quote payload (pure) ────────────────────────────────────

export interface LineItemProperty {
  name: string;
  value: string;
}

/** The payload handed to the basket-add sink at click time. The sink
 *  (index.ts) enriches it with title/price/image via /api/public/product
 *  before calling basket.add — this module stays dep-free of the basket
 *  and the network. */
export interface AddToQuotePayload {
  variantId?: string;
  productId?: string;
  /** Custom line-item options read off the product form (Shopify
   *  `properties[...]` inputs). Shared JSON contract:
   *  Array<{name, value}>. */
  properties?: LineItemProperty[];
}

/** A form that can be queried for many nodes. Real product forms
 *  implement querySelectorAll; node-only test doubles supply a fake. */
interface MultiQueryableLike {
  querySelectorAll?(selectors: string): any;
  querySelector(selectors: string): any;
}

/** Merge two LineItemProperty[] sources for one click — the theme's own
 *  `properties[...]` form inputs (`formProps`, ground truth) plus any
 *  synthesized third-party source like APO v2 selections (`extraProps`).
 *  Form inputs win on a name collision (a merchant's real form field
 *  always outranks a synthesized one); extra-only names pass through
 *  unchanged. `extraProps` empty (no APO / any other source) → returns
 *  `formProps` as-is, so the no-APO case is byte-identical to before
 *  this merge existed.
 *
 *  Exported so option-capture.ts's shared capture helper reuses the same
 *  precedence instead of re-implementing it — one merge rule, one place. */
export function mergeLineItemProperties(formProps: LineItemProperty[], extraProps: LineItemProperty[]): LineItemProperty[] {
  if (!extraProps.length) return formProps;
  const formNames = new Set(formProps.map(p => p.name));
  const merged = formProps.slice();
  for (const p of extraProps) {
    if (formNames.has(p.name)) continue; // theme's own input is ground truth
    merged.push(p);
  }
  return merged;
}

/** Read Shopify line-item properties off the product form — inputs/
 *  selects/textareas named `properties[<Name>]` (the storefront
 *  convention the theme's ATC form already uses for custom options like
 *  engraving/monogram). Skips:
 *   - private props (`properties[_foo]`) — Shopify's own bookkeeping.
 *   - unchecked checkboxes and blank values — nothing the merchant
 *     needs to see on the quote line.
 *  Defensive: any read that throws is skipped, never surfaced. */
export function readLineItemProperties(form: MultiQueryableLike | null | undefined): LineItemProperty[] {
  if (!form) return [];
  const out: LineItemProperty[] = [];
  const seen = new Set<string>();
  let nodes: any[] = [];
  try {
    if (typeof form.querySelectorAll === "function") {
      nodes = Array.from(form.querySelectorAll('[name^="properties["]') as any) || [];
    }
  } catch {
    return [];
  }
  for (const node of nodes) {
    try {
      const rawName: string =
        (typeof node.getAttribute === "function" && node.getAttribute("name")) || node.name || "";
      const m = /^properties\[(.+)\]$/.exec(String(rawName));
      if (!m) continue;
      const name = m[1].trim();
      if (!name || name.startsWith("_")) continue; // private / bookkeeping prop
      if (seen.has(name)) continue;                // first value wins on dup inputs
      const type = String(
        (typeof node.getAttribute === "function" && node.getAttribute("type")) || node.type || "",
      ).toLowerCase();
      // Unchecked checkbox/radio contributes nothing.
      if ((type === "checkbox" || type === "radio") && !node.checked) continue;
      const value = node.value == null ? "" : String(node.value).trim();
      if (!value) continue;
      seen.add(name);
      out.push({ name, value });
    } catch {
      /* skip a hostile node — never throw out of the click path */
    }
  }
  return out;
}

/** Build the Add-to-Quote payload from LIVE form state at click time.
 *  Same variant/product resolution as buildOpenRequestOpts (so ATQ and
 *  form modes agree on which variant the customer selected), plus the
 *  line-item properties.
 *
 *  De-dupe with index.ts's core capture: same contract as
 *  buildOpenRequestOpts above — this `properties` flows through as
 *  payload.properties into handleAddToQuote, which treats a non-empty
 *  array as already-resolved and skips its own page capture. */
export function buildAddToQuotePayload(opts: {
  form: MultiQueryableLike;
  fallbackProductId?: string;
  /** Storefront `window` — probed for APO v2 selections (readApoSelections).
   *  Absent (e.g. existing callers/tests that don't pass it) → no APO
   *  properties, byte-identical to before this adapter existed. */
  win?: any;
  /** Storefront `document` — threaded through to readApoSelections for
   *  parity with its signature; unused today (see apo-adapter.ts). */
  doc?: any;
}): AddToQuotePayload {
  const { form, fallbackProductId, win, doc } = opts;
  const readVal = (sel: string): string => {
    try {
      const el = form.querySelector(sel);
      return el && el.value != null ? String(el.value).trim() : "";
    } catch {
      return "";
    }
  };
  const variantId = readVal(VARIANT_SELECTOR) || readVal("select");
  const productId = readVal(PRODUCT_ID_SELECTOR) || trimStr(fallbackProductId);
  const properties = mergeLineItemProperties(readLineItemProperties(form), readApoSelections(win, doc));
  const out: AddToQuotePayload = {};
  if (variantId) out.variantId = variantId;
  if (productId) out.productId = productId;
  if (properties.length) out.properties = properties;
  return out;
}

// ── DOM-side injection core ────────────────────────────────────────

export interface InjectResult {
  injected: boolean;
  /** "reconciled" (Lane 2/C3): a live button already existed and its
   *  config fingerprint changed — style/label/mode were reapplied to
   *  the SAME node in place (see performInject). "exists": live button
   *  already existed with an unchanged fingerprint — true no-op. */
  reason: "no-form" | "exists" | "ok" | "skipped" | "reconciled";
  button?: any;
}

/** Mirrors the app block's `system_sans` font_mode stack
 *  (request-quote-button.liquid). */
const SYSTEM_FONT_STACK = '-apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif';

/** Shape now controls border-radius ONLY (Phase 1.4) — size (padding/
 *  min-height/font-size/line-height) is decoupled and comes from
 *  computeAnchorSize when an anchor is available (see applySizeStyle).
 *  This fallback table is what a shape used to hardcode for padding —
 *  kept as the no-anchor fallback so byte-identical back-compat holds
 *  for pages where the anchor's computed style can't be read. */
function applyShapeStyle(s: any, shape: ResolvedProductButton["shape"]): void {
  switch (shape) {
    case "pill":
      s.borderRadius = "999px";
      return;
    case "square":
      s.borderRadius = "0";
      return;
    case "inherit":
      // Theme's own class (theme_button/custom_class) controls corner
      // radius — nothing to set.
      return;
    default:
      // "rounded" — legacy default, byte-identical to the original
      // hardcoded pill.
      s.borderRadius = "8px";
  }
}

/** Fallback padding per shape — used only when there's no anchor (or
 *  its computed size can't be read); mirrors what applyShapeStyle used
 *  to hardcode before size/shape were decoupled. */
const SHAPE_FALLBACK_PADDING: Record<Exclude<ResolvedProductButton["shape"], undefined>, string> = {
  rounded: "12px 20px",
  pill: "14px 28px",
  square: "12px 24px",
  inherit: "",
};

interface AnchorSize {
  padding?: string;
  minHeight?: string;
  fontSize?: string;
  lineHeight?: string;
}

/** True when a computed-style length value is a degenerate zero — "0",
 *  "0px", or an all-zero multi-token shorthand ("0px 0px", "0 0 0 0")
 *  — the guard-hole fix (A2/B4): "0px" (and jsdom's unitless "0",
 *  which real browsers never emit but jsdom's incomplete CSSOM does)
 *  are truthy strings, so a naive `if (cs.padding)` copies a genuinely
 *  unset value as if it were real. A mixed value like "0px 20px" is
 *  NOT degenerate — some side really is set. */
function isDegenerateZero(v: string | undefined | null): boolean {
  if (!v) return true;
  const tokens = v.trim().split(/\s+/);
  return tokens.length > 0 && tokens.every(t => /^0(px)?$/.test(t));
}

/** Read the anchor's computed padding/min-height/font-size/line-height
 *  so custom-color/outlined/theme-inverted buttons match the theme
 *  button's dimensions instead of rendering visibly thinner (the "thin
 *  button" bug). Mirrors computeAnchorMargin's defensive pattern:
 *  returns null (caller falls back to fixed values) when there's no
 *  anchor, no getComputedStyle available, nothing usable was read, or
 *  the read throws — never let a theme's DOM break the injector. `win`
 *  is threaded down the same way as computeAnchorMargin so tests can
 *  supply a fake getComputedStyle without jsdom. */
function computeAnchorSize(anchor: any, win?: any): AnchorSize | null {
  if (!anchor) return null;
  if (!isVisibleAnchor(anchor, win)) return null; // hidden/0-rect anchor — fixed fallbacks take over
  try {
    const getCS = resolveGetComputedStyle(win);
    if (!getCS) return null;
    const cs = getCS(anchor);
    if (!cs) return null;
    const out: AnchorSize = {};
    // "0px" is truthy — guard it the same way minHeight already does,
    // or an unpainted/degenerate padding silently copies as real.
    if (!isDegenerateZero(cs.padding)) out.padding = cs.padding;
    if (cs.minHeight && cs.minHeight !== "auto" && !isDegenerateZero(cs.minHeight)) out.minHeight = cs.minHeight;
    if (cs.fontSize) out.fontSize = cs.fontSize;
    if (cs.lineHeight && cs.lineHeight !== "normal") out.lineHeight = cs.lineHeight;
    if (!out.padding && !out.fontSize) return null; // nothing usable to copy
    return out;
  } catch {
    return null;
  }
}

/** Apply size (padding/min-height/font-size/line-height) — the anchor's
 *  computed size when available (so the button matches the theme
 *  button's dimensions, decoupled from `shape`), else the shape's fixed
 *  fallback padding + the legacy fixed font-size/line-height. */
function applySizeStyle(s: any, shape: ResolvedProductButton["shape"], anchorSize: AnchorSize | null): void {
  if (anchorSize) {
    if (anchorSize.padding) s.padding = anchorSize.padding;
    if (anchorSize.minHeight) s.minHeight = anchorSize.minHeight;
    s.fontSize = anchorSize.fontSize || "15px";
    s.lineHeight = anchorSize.lineHeight || "1.2";
    return;
  }
  const pad = SHAPE_FALLBACK_PADDING[shape || "rounded"];
  if (pad) s.padding = pad;
  s.fontSize = "15px";
  s.lineHeight = "1.2";
}

function applyFontStyle(s: any, fontMode: ResolvedProductButton["fontMode"]): void {
  // weight stays constant across both modes — only the family swaps.
  // font-size/line-height moved to applySizeStyle (Phase 1.4, anchor-
  // size aware); weight isn't part of the anchor-size copy.
  s.fontWeight = "600";
  s.fontFamily = fontMode === "system" ? SYSTEM_FONT_STACK : "inherit";
}

function applyWidthStyle(s: any, width: ResolvedProductButton["width"]): void {
  if (width === "full") {
    s.display = "block";
    s.width = "100%";
  } else {
    s.display = "inline-block";
  }
}

/** Read an anchor's class list without assuming a live DOM Element —
 *  className (real Element) falling back to getAttribute("class")
 *  (structural test doubles). */
function readClassName(el: any): string {
  if (!el) return "";
  try {
    if (typeof el.className === "string") return el.className;
    if (typeof el.getAttribute === "function") return el.getAttribute("class") || "";
  } catch {
    /* ignore */
  }
  return "";
}

/** Exact classes stripped from a cloned anchor className — the
 *  payment-cluster's own class name plus the explicit option-app
 *  hide/clone markers this lane's fixtures are built around. */
const STRIP_EXACT_ANCHOR_CLASSES = new Set<string>([
  "shopify-payment-button",
  "additional-checkout-buttons",
  "mw-apo-add-to-cart-hide",
  "mw-apo-add-to-cart-cloned",
]);

/** True for a class that must never end up on OUR injected button
 *  (B3/E1). Mirrors PAYMENT_CLUSTER_SELECTORS' clusters (exact +
 *  prefix + substring forms, since class names don't compose the same
 *  way CSS selectors do) plus any class ending `-hide`/`-hidden` — the
 *  general shape option apps use for their own hide markers, not just
 *  MageWorx's specific one. Theme ATC classes like "add-to-cart" /
 *  "product-form__submit" are deliberately NOT stripped — theme_button
 *  exists to inherit the theme's real button look, which lives in
 *  those classes. The self-hiding problem those classes create is
 *  fixed on the catalog-rules CSS side instead (the
 *  :not([data-qs-autoplace]) exemption below). */
function shouldStripAnchorClass(cls: string): boolean {
  if (STRIP_EXACT_ANCHOR_CLASSES.has(cls)) return true;
  if (cls.startsWith("shopify-payment-button")) return true;
  if (cls.includes("dynamic-checkout")) return true;
  if (cls.endsWith("-hide") || cls.endsWith("-hidden")) return true;
  return false;
}

/** Sanitize a cloned anchor's className before applying it to the
 *  injected button (theme_button/theme_inverted) — strips
 *  payment-cluster + hide/clone-marker classes (see
 *  shouldStripAnchorClass) and collapses whitespace. */
function sanitizeAnchorClass(raw: string): string {
  return raw
    .split(/\s+/)
    .filter(Boolean)
    .filter(cls => !shouldStripAnchorClass(cls))
    .join(" ");
}

/** Default vertical margin (top AND bottom) for every mode except a
 *  successful theme_button anchor-margin copy — enough to clear the
 *  theme's own inter-button gap without knowing its exact spacing.
 *  Applied via the marginTop/marginBottom longhands only (Lane 3 /
 *  F1) — never the `margin` shorthand, which would also pin
 *  margin-left/margin-right to 0 and fight a gap-based flex/grid
 *  parent's own horizontal spacing. */
const DEFAULT_VERTICAL_MARGIN = "8px";

interface AnchorMargin {
  top: string;
  bottom: string;
}

/** theme_button's spacing parity: copy the anchor's own computed
 *  top/bottom margin so the injected button gets EXACTLY the gap the
 *  theme puts between its own buttons. Returns null (caller falls back
 *  to DEFAULT_VERTICAL_MARGIN) when there's no anchor, no
 *  getComputedStyle available (non-browser test environment / hostile
 *  host), or the read throws — never let a theme's DOM break the
 *  injector. `win` is threaded down from autoPlaceProductButton's deps
 *  so tests can supply a fake getComputedStyle without jsdom.
 *
 *  Returns {top, bottom} longhands (Lane 3 / F1) rather than a combined
 *  shorthand string — the caller assigns marginTop/marginBottom
 *  individually and never touches marginLeft/marginRight, so
 *  gap-based flex/grid containers still space this button like their
 *  own children. */
function computeAnchorMargin(anchor: any, win?: any): AnchorMargin | null {
  if (!anchor) return null;
  if (!isVisibleAnchor(anchor, win)) return null; // hidden/0-rect anchor — fixed fallback takes over
  try {
    const getCS = resolveGetComputedStyle(win);
    if (!getCS) return null;
    const cs = getCS(anchor);
    if (!cs) return null;
    // "0px" is truthy — an anchor with no declared margin (top AND
    // bottom both computing to "0px") must fall back to
    // DEFAULT_VERTICAL_MARGIN, not copy a pointless "0px"/"0px" pair.
    const top = !isDegenerateZero(cs.marginTop) ? cs.marginTop : "";
    const bottom = !isDegenerateZero(cs.marginBottom) ? cs.marginBottom : "";
    if (!top && !bottom) return null;
    return { top: top || "0px", bottom: bottom || "0px" };
  } catch {
    return null;
  }
}

/** Parse a computed color string into its {r,g,b,a} channels.
 *  getComputedStyle always resolves to rgb()/rgba() (never a hex/named
 *  color) in real browsers, so that's the only shape handled — plus
 *  the "transparent" keyword some hostile/fake environments might still
 *  hand back. Returns null (unreadable) for anything else so the caller
 *  degrades rather than guessing. */
function parseColorRGBA(v: string | undefined | null): { r: number; g: number; b: number; a: number } | null {
  if (!v) return null;
  const s = String(v).trim();
  if (!s) return null;
  if (s === "transparent") return { r: 0, g: 0, b: 0, a: 0 };
  const m = s.match(/^rgba?\(([^)]+)\)$/i);
  if (!m) return null;
  const parts = m[1].split(",").map(p => parseFloat(p.trim()));
  if (parts.length < 3 || parts.slice(0, 3).some(p => !Number.isFinite(p))) return null;
  const a = parts.length === 4 ? parts[3] : 1;
  return { r: parts[0], g: parts[1], b: parts[2], a: Number.isFinite(a) ? a : 1 };
}

/** Rough perceptual distance between two opaque colors — sum of
 *  absolute per-channel deltas. Used to tell "this bg is basically the
 *  page's own color" (effectively outlined, even if technically
 *  opaque) apart from "this is a genuinely distinct fill" (effectively
 *  filled). Real filled buttons are almost always far apart on this
 *  scale; page-tint variance (off-white cards, subtle greys) stays
 *  well under the threshold. */
function colorDistance(a: { r: number; g: number; b: number }, b: { r: number; g: number; b: number }): number {
  return Math.abs(a.r - b.r) + Math.abs(a.g - b.g) + Math.abs(a.b - b.b);
}
const BG_MATCH_THRESHOLD = 24;

/** YIQ-style luminance pick for contrast text — dark fill gets white
 *  text, light fill gets near-black text. Falls back to white (the old
 *  hardcoded behavior) when the color can't be parsed. */
function pickContrastText(color: string): string {
  const c = parseColorRGBA(color);
  if (!c) return "#ffffff";
  const yiq = (c.r * 299 + c.g * 587 + c.b * 114) / 1000;
  return yiq > 128 ? "#111111" : "#ffffff";
}

/** Tolerant parent-walk step — real DOM exposes parentElement (skips
 *  non-Element ancestors like #document), the node-only test double
 *  only has parentNode. Either is fine for this walk. */
function getParent(el: any): any {
  return (el && (el.parentElement || el.parentNode)) || null;
}

/** Resolve the "page/surface" background behind an anchor: walk up
 *  parents (capped, guarded) for the first ancestor with a readable,
 *  non-transparent background, then fall back to doc.body / the root
 *  element. Returns null when nothing is readable — the caller then
 *  keeps the plain alpha-only heuristic instead of guessing. */
function resolvePageBg(anchor: any, getCS: (el: any) => any, doc: any): { r: number; g: number; b: number } | null {
  // Defense-in-depth: computeInverseColors already gates on the anchor's
  // own visibility before ever reaching here, but a hidden anchor's
  // "surrounding page" is meaningless to compute regardless of caller.
  if (!isVisibleAnchor(anchor, { getComputedStyle: getCS })) return null;
  let node = getParent(anchor);
  for (let hops = 0; node && hops < 6; hops++, node = getParent(node)) {
    try {
      const cs = getCS(node);
      const c = cs && parseColorRGBA(cs.backgroundColor);
      if (c && c.a > 0.05) return c;
    } catch {
      /* keep walking */
    }
  }
  for (const el of [doc && doc.body, doc && doc.documentElement]) {
    if (!el) continue;
    try {
      const cs = getCS(el);
      const c = cs && parseColorRGBA(cs.backgroundColor);
      if (c && c.a > 0.05) return c;
    } catch {
      /* try the next fallback */
    }
  }
  return null;
}

/** A "real" visible border — not the CSS-default none/0px. Every field
 *  is optional on the computed-style shape (test doubles + odd host
 *  CSSOM), so absence of a disqualifier counts as visible; only an
 *  explicit none/hidden style or 0px width rules it out. */
function hasVisibleBorder(cs: any): boolean {
  if (!cs || !cs.borderColor) return false;
  if (cs.borderStyle === "none" || cs.borderStyle === "hidden") return false;
  if (cs.borderTopWidth === "0px" || cs.borderTopWidth === "0") return false;
  return true;
}

interface InverseColor {
  /** "filled" = the anchor is effectively a solid-fill button (opaque
   *  bg meaningfully different from the page) — the inverse is
   *  transparent bg + the anchor's own color as border/text.
   *  "outlined" = the anchor is effectively outlined (transparent bg,
   *  OR an opaque bg that just matches the page/surface color) — the
   *  inverse is a solid fill using the anchor's own border/text
   *  color. */
  kind: "filled" | "outlined";
  color: string;
}

/** Read the anchor's OWN computed colors (backgroundColor/color/
 *  borderColor) and determine the inverse — the core of "Match theme
 *  button — inverted" (theme_inverted). Filled-vs-outlined is NOT just
 *  background alpha: an opaque background that matches the surrounding
 *  page/surface color (an "outlined" button whose fill happens to be
 *  opaque white on a white page, say) is effectively outlined too —
 *  see resolvePageBg. Falls back to the plain alpha-only rule when the
 *  page background itself can't be read. Returns null — the caller
 *  degrades to plain "theme_button" — when there's no anchor, no
 *  getComputedStyle, the background can't be parsed, or the resolved
 *  color to flip is empty; never throws. */
function computeInverseColors(anchor: any, win?: any, doc?: any): InverseColor | null {
  if (!anchor) return null;
  if (!isVisibleAnchor(anchor, win)) return null; // hidden/0-rect anchor — degrades to theme_button
  try {
    const getCS = resolveGetComputedStyle(win);
    if (!getCS) return null;
    const cs = getCS(anchor);
    if (!cs) return null;
    const bg = parseColorRGBA(cs.backgroundColor);
    if (!bg) return null;

    let effectivelyFilled = bg.a > 0.05;
    if (effectivelyFilled) {
      const pageBg = resolvePageBg(anchor, getCS, doc);
      if (pageBg && colorDistance(bg, pageBg) <= BG_MATCH_THRESHOLD) effectivelyFilled = false;
    }

    if (effectivelyFilled) {
      // Filled theme button — C is its own background color.
      if (!cs.backgroundColor) return null;
      return { kind: "filled", color: cs.backgroundColor };
    }
    // Effectively outlined — C is the visible border color if there
    // is one, else its own text color.
    const c = (hasVisibleBorder(cs) && cs.borderColor) || cs.color || cs.borderColor;
    if (!c) return null;
    return { kind: "outlined", color: c };
  } catch {
    return null;
  }
}

/** Apply the inverse-color declarations for theme_inverted — flips
 *  filled↔outlined using the anchor's OWN color (see
 *  computeInverseColors). Mirrors the manual "outlined" mode's
 *  1.5px border weight for the filled→outlined flip. Contrast text on
 *  the outlined→filled flip is picked by C's luminance (not hardcoded
 *  white) so a light fill color doesn't produce invisible light-on-
 *  light text. */
function applyInverseColorStyle(s: any, inverse: InverseColor): void {
  if (inverse.kind === "filled") {
    s.background = "transparent";
    s.backgroundColor = "transparent";
    s.color = inverse.color;
    s.border = "1.5px solid " + inverse.color;
  } else {
    s.background = inverse.color;
    s.backgroundColor = inverse.color;
    s.color = pickContrastText(inverse.color);
    s.border = "none";
  }
}

// ── Cascade authority (Lane 3 / B2) ───────────────────────────────
// Plain inline styles lose to a theme's own `button { ... } !important`
// rule at equal-or-lower specificity in source order — the LAST way
// "outlined" (or any color/outlined look) can still fail to render even
// after the reconcile fix (Lane 2). An attribute-selector rule appended
// LAST to <head> wins regardless of the theme's own rule order, so the
// injected stylesheet (see injectAutoPlaceStyleTag) carries the same
// declarations applyButtonStyle already sets inline, mirrored with
// `!important`. Belt-and-suspenders: the inline styles stay too — some
// themes strip foreign inline styles, so the plain inline values are
// still the first line of defense.

/** [inline style prop, CSS property] pairs carried into the cascade
 *  sheet for "color"/"outlined" — exactly the properties
 *  applyButtonStyle computes for those two modes. Deliberately excludes
 *  layout-only properties (display/textAlign/whiteSpace/textDecoration/
 *  appearance/cursor) — not the "look" a theme's `!important` would
 *  fight — and marginLeft/marginRight, which are never set at all (Lane
 *  3 / F1's horizontal-margin fix). */
const COLOR_MODE_CASCADE_PROPS: ReadonlyArray<readonly [string, string]> = [
  ["background", "background"],
  ["backgroundColor", "background-color"],
  ["color", "color"],
  ["border", "border"],
  ["borderRadius", "border-radius"],
  ["padding", "padding"],
  ["minHeight", "min-height"],
  ["fontFamily", "font-family"],
  ["fontWeight", "font-weight"],
  ["fontSize", "font-size"],
  ["lineHeight", "line-height"],
  ["marginTop", "margin-top"],
  ["marginBottom", "margin-bottom"],
];

/** theme_inverted carries ONLY its computed color overrides into the
 *  sheet — size/font/margin there come from the cloned theme class, not
 *  inline, so there's nothing else to assert. */
const THEME_INVERTED_CASCADE_PROPS: ReadonlyArray<readonly [string, string]> = [
  ["background", "background"],
  ["backgroundColor", "background-color"],
  ["color", "color"],
  ["border", "border"],
];

/** Build the `!important` declaration lines for whichever prop list
 *  applies, reading the ALREADY-APPLIED inline values off `s` so the
 *  sheet can never drift from what's actually inline. Skips falsy
 *  values (never emits `color: !important;`). */
function buildCascadeDeclarations(props: ReadonlyArray<readonly [string, string]>, s: any): string[] {
  const lines: string[] = [];
  for (const [jsProp, cssProp] of props) {
    const v = s[jsProp];
    if (v) lines.push(`  ${cssProp}: ${v} !important;`);
  }
  return lines;
}

/** One `button[data-qs-autoplace] { ... }` rule keyed on the identity
 *  attribute — attribute selector + `!important`, appended last to
 *  <head>, beats a theme's own `button {...} !important` at
 *  equal-or-lower specificity regardless of source order. Returns null
 *  (nothing to inject) when there are no declarations at all — the
 *  caller then clears/removes any previously-injected sheet instead of
 *  leaving a stale empty rule. `includeWidth` carries "width when full"
 *  (color/outlined only, per the kickoff spec — theme_inverted's
 *  narrower color-only sheet never sets width). */
function buildCascadeRuleCss(
  props: ReadonlyArray<readonly [string, string]>,
  s: any,
  includeWidthFull: boolean,
): string | null {
  const lines = buildCascadeDeclarations(props, s);
  if (includeWidthFull) lines.push("  width: 100% !important;");
  if (!lines.length) return null;
  return `button[${AUTO_PLACE_BTN_ATTR}] {\n${lines.join("\n")}\n}`;
}

/** Apply the resolved style axes to the injected button. Back-compat:
 *  unset config (style_mode "color" + shape "rounded" + width "auto" +
 *  font_mode "inherit") produces the SAME look declarations as the
 *  original hardcoded pill (the vertical margin now also applies —
 *  see the module doc's spacing-fix note — so it's no longer literally
 *  byte-identical, but every color/shape/font/layout declaration is).
 *
 *  style_mode axis:
 *    - "color" (default): bg_color/text_color, THEN size (anchor-copied
 *      or fixed fallback, Phase 1.4) + shape (radius only) + font.
 *    - "outlined": the inverted twin of "color" — transparent
 *      background, bg_color as the border + text color instead — same
 *      size/shape/font axes.
 *    - "theme_button": clone the anchor's own (sanitized — see
 *      sanitizeAnchorClass) CSS classes onto the button and drop ALL
 *      inline chrome — color/size/shape/font AND layout
 *      (display/whiteSpace/textAlign/textDecoration/appearance, B1
 *      fix) — so the theme's native button CSS fully styles it, unrestricted
 *      by anything this module would otherwise force inline. The
 *      killer option for matching a theme's full-width/rectangular
 *      buttons. Degrades to "color" when there's no anchor (end-of-form
 *      append fallback) or the anchor carries no classes to clone.
 *    - "theme_inverted": clones the anchor's classes like theme_button
 *      (same chrome-drop), THEN inline-overrides color to the
 *      INVERSE of the anchor's own computed color (filled↔outlined —
 *      see computeInverseColors/applyInverseColorStyle). Degrades to
 *      "theme_button" when the anchor's colors can't be read (which
 *      itself degrades further to "color" with no anchor/classes) —
 *      heuristic (alpha-based filled/outlined detection), never throws,
 *      never renders unstyled.
 *    - "custom_class": same chrome-drop as theme_button, but applies
 *      button_class (merchant-pasted classes) instead of cloning.
 *
 *  Size: "color"/"outlined" copy the anchor's own computed padding/
 *  min-height/font-size/line-height (computeAnchorSize) so the button
 *  matches the theme button's dimensions instead of rendering thinner;
 *  `shape` now controls border-radius only. No anchor (or unreadable/
 *  hidden computed style) falls back to the shape's fixed legacy
 *  padding + 15px/1.2 font-size/line-height.
 *
 *  Margin: theme_button/theme_inverted copy the anchor's own computed
 *  vertical margin (exact theme match, see computeAnchorMargin); every
 *  other mode — custom_class, color, outlined, and the no-anchor/
 *  no-classes degrade — gets DEFAULT_VERTICAL_MARGIN so the button is
 *  never flush against the next element.
 *
 *  width "full" forces full width in every mode, but HOW differs
 *  (B1 fix): "color"/"outlined" get the full applyWidthStyle pair
 *  (display:block + width:100%) as part of their normal inline
 *  layout. theme_button/theme_inverted/custom_class set ONLY
 *  width:100% — never a display override, since forcing display:block
 *  on a theme's own class (which might be flex/inline-flex/grid) would
 *  reintroduce the exact "fight the theme's layout" bug this lane
 *  fixes; width "auto" (default) sets nothing at all for those three
 *  modes, leaving layout entirely to the cloned/pasted class. Cursor is
 *  the only "look" property applied unconditionally in every mode.
 *
 *  Returns the cascade-authority `!important` stylesheet rule text
 *  (Lane 3 / B2) for "color"/"outlined"/an applied "theme_inverted", or
 *  null for "theme_button"/"custom_class" (the theme's own CSS is the
 *  point there — no injected chrome rules) — see
 *  COLOR_MODE_CASCADE_PROPS/THEME_INVERTED_CASCADE_PROPS. The caller
 *  (performInject) feeds this straight to injectAutoPlaceStyleTag. */
function applyButtonStyle(btn: any, r: ResolvedProductButton, anchor?: any, win?: any, doc?: any): string | null {
  const s = btn && btn.style;
  if (!s) return null;

  // Structural, not "look" — applied unconditionally in every mode.
  s.cursor = "pointer";

  const anchorClass = sanitizeAnchorClass(readClassName(anchor));

  // theme_inverted resolves its own color-flip up front (needs a real
  // anchor + readable colors) and degrades to plain theme_button
  // (which itself degrades further to "color" below when there's no
  // anchorClass) rather than ever rendering unstyled.
  let mode: ResolvedProductButton["styleMode"] = r.styleMode;
  let inverse: InverseColor | null = null;
  if (mode === "theme_inverted") {
    inverse = anchorClass ? computeInverseColors(anchor, win, doc) : null;
    mode = inverse ? "theme_inverted" : "theme_button";
  }
  if (mode === "theme_button" && !anchorClass) mode = "color";

  if (mode === "theme_button" || mode === "theme_inverted" || mode === "custom_class") {
    // B1 fix: these three modes exist SPECIFICALLY so the theme's own
    // CSS (or the merchant's own button_class CSS) governs layout —
    // display/whiteSpace/textAlign/textDecoration/appearance used to
    // be set unconditionally below, silently fighting a cloned theme
    // button's own layout (e.g. overriding a flex/grid button back to
    // inline-block). Only class list + margin + cursor (above) are
    // set here, plus an explicit width:100% when the merchant forced
    // width "full" — never a display override, even then.
    if (mode === "custom_class") {
      const cls = r.buttonClass.trim();
      btn.className = cls ? AUTO_PLACE_BTN_CLASS + " " + cls : AUTO_PLACE_BTN_CLASS;
      s.marginTop = DEFAULT_VERTICAL_MARGIN;
      s.marginBottom = DEFAULT_VERTICAL_MARGIN;
    } else {
      btn.className = AUTO_PLACE_BTN_CLASS + " " + anchorClass;
      const anchorMargin = computeAnchorMargin(anchor, win);
      s.marginTop = anchorMargin ? anchorMargin.top : DEFAULT_VERTICAL_MARGIN;
      s.marginBottom = anchorMargin ? anchorMargin.bottom : DEFAULT_VERTICAL_MARGIN;
    }
    if (r.width === "full") s.width = "100%";
    if (mode === "theme_inverted") {
      applyInverseColorStyle(s, inverse as InverseColor);
      return buildCascadeRuleCss(THEME_INVERTED_CASCADE_PROPS, s, false);
    }
    return null; // theme_button/custom_class — the theme's own CSS is the point
  }

  // "color" / "outlined" — full inline chrome + layout (back-compat).
  s.textAlign = "center";
  s.whiteSpace = "nowrap";
  s.textDecoration = "none";
  s.appearance = "none";
  s.webkitAppearance = "none";
  applyWidthStyle(s, r.width);
  s.marginTop = DEFAULT_VERTICAL_MARGIN;
  s.marginBottom = DEFAULT_VERTICAL_MARGIN;

  const anchorSize = computeAnchorSize(anchor, win);

  if (mode === "outlined") {
    s.background = "transparent";
    s.backgroundColor = "transparent";
    s.color = r.bgColor;
    s.border = "1.5px solid " + r.bgColor;
    applyShapeStyle(s, r.shape);
    applySizeStyle(s, r.shape, anchorSize);
    applyFontStyle(s, r.fontMode);
    return buildCascadeRuleCss(COLOR_MODE_CASCADE_PROPS, s, r.width === "full");
  }

  // "color" — today's look, plus the size/shape/font axes.
  s.background = r.bgColor;
  s.backgroundColor = r.bgColor;
  s.color = r.textColor;
  s.border = "none";
  applyShapeStyle(s, r.shape);
  applySizeStyle(s, r.shape, anchorSize);
  applyFontStyle(s, r.fontMode);
  return buildCascadeRuleCss(COLOR_MODE_CASCADE_PROPS, s, r.width === "full");
}

// ── Cluster-aware insertion (Lane 3 / E2.2, F1, F2) ────────────────
// Sibling insertion between the ATC and the payment cluster
// (<shopify-payment-button>, Shop Pay/Apple Pay/PayPal express) breaks
// the sibling/nth-child selectors themes use to show/space that
// cluster — the live "dynamic checkout disappeared" bug. And inserting
// bare into a flex/grid button row squeezes/overflows all the buttons
// instead of taking its own row. Both are fixed at the insertion point,
// never at anchor resolution (Lane 1) — the anchor itself is unchanged.

export const AUTO_PLACE_WRAP_ATTR = "data-qs-autoplace-wrap";

/** The next ELEMENT sibling (skipping text/comment nodes) — prefers the
 *  real `nextElementSibling` (real DOM/jsdom); falls back to plain
 *  `nextSibling` for structural test doubles that only implement that
 *  (their fake trees never carry whitespace text nodes anyway, so the
 *  two are equivalent there). Never throws. */
function getNextElement(el: any): any {
  if (!el) return null;
  try {
    if (typeof el.nextElementSibling !== "undefined") return el.nextElementSibling;
    return el.nextSibling || null;
  } catch {
    return null;
  }
}

/** True when `el` ITSELF (not an ancestor — this is a sibling check,
 *  not the anchor-exclusion walk) matches one of PAYMENT_CLUSTER_SELECTORS.
 *  Reuses `closest` (self-matches first) rather than a separate
 *  `matches` call so structural test doubles that implement `closest`
 *  but not `matches` still get this check. */
function isPaymentClusterNode(el: any): boolean {
  if (!el) return false;
  for (const sel of PAYMENT_CLUSTER_SELECTORS) {
    try {
      if (typeof el.closest === "function" && el.closest(sel) === el) return true;
    } catch {
      /* malformed/unsupported selector on this engine — skip */
    }
  }
  return false;
}

/** Resolve the actual "insert before" node for `after` placement (E2.2):
 *  the anchor's next element sibling, UNLESS that's the payment
 *  cluster — then skip past it (and any further consecutive cluster
 *  siblings — some themes render Shop Pay and PayPal/express as two
 *  separate sibling blocks) so our button never lands between the ATC
 *  and the cluster. `before` placement is unaffected (this is only
 *  called for `after`). */
function resolveAfterInsertionNode(anchor: any): any {
  let node = getNextElement(anchor);
  while (isPaymentClusterNode(node)) {
    node = getNextElement(node);
  }
  return node;
}

/** Resolve `getComputedStyle(parent).display`, or null when
 *  unreadable — the signal for whether the insertion parent is a
 *  flex/grid container that would squeeze a bare block-level sibling. */
function computeParentDisplay(parent: any, win?: any): string | null {
  if (!parent) return null;
  try {
    const getCS = resolveGetComputedStyle(win);
    if (!getCS) return null;
    const cs = getCS(parent);
    return cs && typeof cs.display === "string" ? cs.display : null;
  } catch {
    return null;
  }
}

function isFlexOrGridDisplay(display: string | null): boolean {
  return display === "flex" || display === "inline-flex" || display === "grid" || display === "inline-grid";
}

/** Layout-neutral wrapper (F1): when the insertion PARENT is flex/grid,
 *  wrap the button in a minimal block `<div data-qs-autoplace-wrap>` so
 *  it takes its own row instead of becoming a squeezed third flex/grid
 *  item. Plain block parents get the bare button back, unchanged —
 *  byte-identical to before this lane. Never throws — a wrapper failure
 *  falls back to the bare button rather than blocking insertion. The
 *  wrapper carries AUTO_PLACE_WRAP_ATTR so ghost-sweep/teardown remove
 *  it together with the button (see collectByAttr/removeAutoPlaceButtons). */
function maybeWrapForLayout(doc: DocumentLike, btn: any, parent: any, win?: any): any {
  const display = computeParentDisplay(parent, win);
  if (!isFlexOrGridDisplay(display)) return btn;
  try {
    const wrap = (doc as any).createElement("div");
    wrap.setAttribute(AUTO_PLACE_WRAP_ATTR, "1");
    const s = wrap.style;
    if (s) {
      s.flexBasis = "100%";
      s.width = "100%";
      s.minWidth = "0";
      if (display === "grid" || display === "inline-grid") s.gridColumn = "1 / -1";
    }
    wrap.appendChild(btn);
    return wrap;
  } catch {
    return btn;
  }
}

/** Append fallback (F2): when no anchor resolves, prefer landing INSIDE
 *  the last visible button/role=button container in the form (the
 *  parent of the last visible one) over the raw form end — a theme
 *  commonly has hidden bookkeeping inputs after its real button row, so
 *  a blind form-append can land visually detached from every other
 *  control. Excludes the payment cluster (never a valid container to
 *  join). Returns null (caller keeps today's plain form-append) when no
 *  such container is found. Queried as separate selectors, not a
 *  combinator list, so the node-only structural test doubles (whose
 *  tiny selector parser has no combinator support) can exercise this
 *  too. */
function findAppendContainer(form: any, win?: any): any {
  try {
    if (typeof form.querySelectorAll !== "function") return null;
    const candidates: any[] = [
      ...Array.from(form.querySelectorAll("button") as any),
      ...Array.from(form.querySelectorAll('a[role="button"]') as any),
      ...Array.from(form.querySelectorAll('[role="button"]') as any),
    ];
    const seen = new Set<any>();
    for (let i = candidates.length - 1; i >= 0; i--) {
      const b = candidates[i];
      if (seen.has(b)) continue;
      seen.add(b);
      if (isExcludedAnchor(b)) continue; // never join the payment cluster
      if (!isVisibleAnchor(b, win)) continue;
      const parent = getParent(b);
      if (parent) return parent;
    }
  } catch {
    /* ignore — never let a hostile form crash resolution */
  }
  return null;
}

/** Insert the button (or its layout wrapper) relative to the resolved
 *  placement. Returns the node actually inserted into the DOM — `btn`
 *  itself in a plain block parent (byte-identical to before this lane),
 *  or the wrapper div in a flex/grid parent — so the caller can track
 *  it for ghost-sweep/teardown. */
function insertButton(doc: DocumentLike, form: any, placement: Placement, btn: any, win?: any): any {
  const { mode } = placement;
  // ElementLike is intentionally query-only; the insertion uses live
  // Node members (parentNode / nextSibling / insertBefore) so read
  // them off the concrete node.
  const anchor = placement.anchor as any;
  try {
    if (mode !== "append" && anchor && anchor.parentNode) {
      const parent = anchor.parentNode;
      const nodeToInsert = maybeWrapForLayout(doc, btn, parent, win);
      if (mode === "before") {
        parent.insertBefore(nodeToInsert, anchor);
        return nodeToInsert;
      }
      // "after" — skip past the payment cluster (E2.2) instead of
      // splitting the ATC/cluster sibling pair.
      const insertBeforeNode = resolveAfterInsertionNode(anchor);
      parent.insertBefore(nodeToInsert, insertBeforeNode || null);
      return nodeToInsert;
    }
  } catch {
    /* fall through to end-of-form append */
  }
  try {
    const container = findAppendContainer(form, win) || form;
    const nodeToInsert = maybeWrapForLayout(doc, btn, container, win);
    container.appendChild(nodeToInsert);
    return nodeToInsert;
  } catch {
    /* give up silently — never throw */
  }
  return btn;
}

// ── Reconcile support (Lane 2 / C3, C2, E3) ───────────────────────

/** Every field of a ResolvedProductButton that affects the injected
 *  button's RENDER — what applyButtonStyle draws plus the label text.
 *  Deliberately excludes mode/formId/openMode/displayMode/
 *  targetSelector/position: those drive CLICK-time behavior and
 *  placement, not what's on screen, and the click handler already
 *  reads them live off `_cur*` refs on every fire — no reconcile
 *  needed for them to take effect. */
function computeButtonFingerprint(r: ResolvedProductButton): string {
  return JSON.stringify([
    r.label,
    r.bgColor,
    r.textColor,
    r.styleMode,
    r.buttonClass,
    r.width,
    r.shape,
    r.fontMode,
  ]);
}

/** Every inline style property ANY style_mode branch of applyButtonStyle
 *  may set. Reconcile clears all of them (plus className, back to the
 *  bare identity class) before reapplying — without this, re-running
 *  applyButtonStyle for a NEW mode only ADDS declarations on top of the
 *  old ones instead of replacing them, which is exactly the "outlined
 *  doesn't carry" root cause once a restyle path exists at all: e.g.
 *  flipping "outlined" (transparent bg + border) back to "color" would
 *  set the new background but leave the old border in place forever.
 *  `cursor` is excluded — every mode sets it to the same "pointer"
 *  unconditionally, so there's nothing stale to clear. */
const MANAGED_STYLE_PROPS: readonly string[] = [
  "textAlign", "whiteSpace", "textDecoration", "appearance", "webkitAppearance",
  "display", "width", "marginTop", "marginBottom", "background", "backgroundColor", "color",
  "border", "borderRadius", "padding", "minHeight", "fontSize", "lineHeight",
  "fontWeight", "fontFamily",
];

function resetButtonChrome(btn: any): void {
  try {
    btn.className = AUTO_PLACE_BTN_CLASS;
  } catch {
    /* ignore */
  }
  const s = btn && btn.style;
  if (!s) return;
  for (const prop of MANAGED_STYLE_PROPS) {
    try {
      s[prop] = "";
    } catch {
      /* ignore a hostile/readonly style property */
    }
  }
}

/** Every node in the document carrying `attr` — real nodes AND ghost
 *  clones alike. Shared by the button identity attribute (B3) and the
 *  wrapper identity attribute (Lane 3 / F1) so both get the same
 *  ghost-sweep/teardown treatment via one primitive. */
function collectByAttr(doc: any, attr: string): any[] {
  try {
    if (typeof doc.querySelectorAll === "function") {
      return Array.from(doc.querySelectorAll("[" + attr + "]") as any) as any[];
    }
    const one = doc.querySelector ? doc.querySelector("[" + attr + "]") : null;
    return one ? [one] : [];
  } catch {
    return [];
  }
}

/** Every [data-qs-autoplace] node currently in the document — real
 *  buttons AND ghost clones alike. Never queries by class (a
 *  theme_button clone can legitimately share the anchor's own class),
 *  only the identity attribute we control (B3). */
function collectAutoPlaceNodes(doc: any): any[] {
  return collectByAttr(doc, AUTO_PLACE_BTN_ATTR);
}

function removeNode(n: any): void {
  try {
    if (n && n.parentNode && typeof n.parentNode.removeChild === "function") {
      n.parentNode.removeChild(n);
    }
  } catch {
    /* ignore individual removal failure */
  }
}

/** Inject (or update) the cascade-authority `<style data-qs-autoplace-
 *  style>` sheet (Lane 3 / B2) — mirrors catalog-rules.ts's own
 *  injected-style-element idiom (get-or-create by identity, overwrite
 *  textContent). `cssText` null clears/removes any previously-injected
 *  sheet instead of leaving stale rules (current mode carries no
 *  cascade rules — theme_button/custom_class, or no button at all).
 *  Falls back to documentElement/body when `doc.head` is unavailable
 *  (node-only structural test doubles) — never throws. */
function injectAutoPlaceStyleTag(doc: any, cssText: string | null): void {
  try {
    if (!cssText) {
      removeAutoPlaceStyleTag(doc);
      return;
    }
    let tag = typeof doc.querySelector === "function" ? doc.querySelector("[" + AUTO_PLACE_STYLE_ATTR + "]") : null;
    if (!tag) {
      if (typeof doc.createElement !== "function") return;
      tag = doc.createElement("style");
      tag.setAttribute(AUTO_PLACE_STYLE_ATTR, "1");
      const parent = doc.head || doc.documentElement || doc.body;
      if (!parent || typeof parent.appendChild !== "function") return;
      parent.appendChild(tag);
    }
    tag.textContent = cssText;
  } catch {
    /* never let cascade-sheet injection break the button injector */
  }
}

function removeAutoPlaceStyleTag(doc: any): void {
  try {
    const tag = typeof doc.querySelector === "function" ? doc.querySelector("[" + AUTO_PLACE_STYLE_ATTR + "]") : null;
    if (tag) removeNode(tag);
  } catch {
    /* ignore */
  }
}

/** Inject the button once, or RECONCILE it in place when it already
 *  exists and the config changed (C3 — the "outlined doesn't carry"
 *  root cause). The dedup guard is DOCUMENT-WIDE (one auto-place
 *  button per page, period), not scoped to whichever form
 *  `findProductForm` happens to pick. A custom `target_selector` may
 *  resolve to an anchor that lives in a DIFFERENT `/cart/add` form than
 *  the initial pick (or outside any form); scoping the guard to the
 *  wrong form let the MutationObserver re-inject forever (the
 *  runaway-duplication bug). Graceful (no-op when there's no product
 *  form on the page at all). The click handler reads the variant live
 *  and calls openRequest. Pure-ish: all DOM access goes through the
 *  injected `doc`, so a node-only test can drive it with a fake
 *  document.
 *
 *  Existence is no longer a bare `hasAutoPlaceButton` DOM query (E3):
 *  the module tracks ITS OWN live node (`_liveButton`) rather than
 *  trusting any `[data-qs-autoplace]` match — a sticky-ATC app's
 *  `cloneNode(true)` of the form carries our attribute on a dead,
 *  listener-less copy that would otherwise satisfy a DOM-only guard
 *  forever. Every `[data-qs-autoplace]` node that ISN'T our tracked ref
 *  is a ghost and gets removed outright (never touches anything
 *  lacking the attribute). If OUR node is still attached, `performInject`
 *  reconciles (fingerprint changed) or no-ops (unchanged); if it's
 *  gone, a fresh button is created. */
export function performInject(
  doc: DocumentLike,
  resolved: ResolvedProductButton,
  openRequest: (opts: OpenRequestOpts) => void,
  getPageProductId?: () => string | undefined,
  win?: any,
  addToQuote?: (payload: AddToQuotePayload) => void,
): InjectResult {
  // C2: refresh the "current call" refs unconditionally, before any
  // early return — the click handler and the reassert observer read
  // these at FIRE time instead of closing over whatever call created
  // them, so a later call's fresh config/sinks are what actually run,
  // never a stale one.
  _curResolved = resolved;
  _curOpenRequest = openRequest;
  _curGetPageProductId = getPageProductId;
  _curWin = win;
  _curAddToQuote = addToQuote;

  // E3: ghost-clone defense. Remove every [data-qs-autoplace] node
  // that ISN'T our tracked live node — care: this only ever touches
  // nodes carrying our identity attribute (never theme nodes). Same
  // treatment for the layout wrapper (Lane 3 / F1): a co-tenant's
  // cloneNode(true) of our button also clones its wrapper, and that
  // ghost wrapper carries AUTO_PLACE_WRAP_ATTR too.
  const attrNodes = collectAutoPlaceNodes(doc);
  const ourNodeAttached = !!_liveButton && attrNodes.indexOf(_liveButton) !== -1;
  for (const n of attrNodes) {
    if (n !== _liveButton) removeNode(n);
  }
  const wrapNodes = collectByAttr(doc, AUTO_PLACE_WRAP_ATTR);
  for (const n of wrapNodes) {
    if (n !== _liveWrapper) removeNode(n);
  }

  if (ourNodeAttached) {
    // C3: reconcile instead of exists-bail. Placement/anchor are NOT
    // re-evaluated here (anchor moves are Lane 3's concern) — only
    // style/label/mode. Mode/sink rebinding needs no extra code: the
    // click handler below always reads the just-refreshed `_cur*` refs
    // at fire time, so a mode flip takes effect immediately regardless
    // of whether the render fingerprint changed.
    const fingerprint = computeButtonFingerprint(resolved);
    if (fingerprint !== _liveFingerprint) {
      resetButtonChrome(_liveButton);
      const cascadeCss = applyButtonStyle(_liveButton, resolved, _liveAnchor, win, doc);
      injectAutoPlaceStyleTag(doc, cascadeCss);
      _liveButton.textContent = resolved.label;
      _liveFingerprint = fingerprint;
      return { injected: false, reason: "reconciled", button: _liveButton };
    }
    return { injected: false, reason: "exists", button: _liveButton };
  }

  // Our node is gone (never created, removed by a re-render/co-tenant,
  // or was only ever a ghost that just got swept above) — (re)create.
  _liveButton = null;
  _liveFingerprint = null;
  _liveAnchor = null;
  _liveWrapper = null;

  const initialForm = findProductForm(doc);
  if (!initialForm) return { injected: false, reason: "no-form" };

  const placement = resolvePlacement({
    form: initialForm,
    doc,
    targetSelector: resolved.targetSelector || undefined,
    position: resolved.position,
    win,
  });

  // Inject into the ANCHOR's own form — not necessarily initialForm —
  // so the guard, the form (used for the click-time variant read and
  // the end-of-form append fallback), and the anchor all share one
  // scope. Fall back to initialForm when there's no anchor, or the
  // anchor sits outside any /cart/add form (a custom target_selector
  // may legitimately point outside the form entirely).
  const anchor = placement.anchor as any;
  const anchorForm = anchor && typeof anchor.closest === "function"
    ? (() => {
        try {
          return anchor.closest(PRODUCT_FORM_SELECTOR);
        } catch {
          return null;
        }
      })()
    : null;
  const form = anchorForm || initialForm;

  const btn = doc.createElement("button");
  // type="button" is CRITICAL — the button lives INSIDE the
  // add-to-cart <form>; the default type="submit" would POST the cart
  // form (add the product to cart) on click.
  btn.setAttribute("type", "button");
  btn.className = AUTO_PLACE_BTN_CLASS;
  btn.setAttribute(AUTO_PLACE_BTN_ATTR, "1");
  btn.textContent = resolved.label;
  const cascadeCss = applyButtonStyle(btn, resolved, placement.anchor, win, doc);
  injectAutoPlaceStyleTag(doc, cascadeCss);

  btn.addEventListener("click", (ev?: any) => {
    // Belt-and-suspenders with type="button": stop any theme handler
    // from treating this as a cart submit.
    try {
      if (ev && typeof ev.preventDefault === "function") ev.preventDefault();
    } catch {
      /* ignore */
    }
    try {
      // C2: read the CURRENT `_cur*` refs, not the `resolved`/
      // `openRequest`/`getPageProductId`/`win`/`addToQuote` this
      // listener closed over at creation time — a later reconcile (or
      // even a same-fingerprint no-op call) may have flipped `mode`
      // (form ↔ add_to_quote) or rewired the sinks, and this ONE
      // long-lived listener must reflect that without ever being
      // replaced. `form`/`doc` stay closed-over: placement isn't
      // re-evaluated on reconcile (Lane 3's concern), so the anchor's
      // own form never changes under an existing button.
      const liveResolved = _curResolved as ResolvedProductButton;
      const liveAddToQuote = _curAddToQuote;
      const liveOpenRequest = _curOpenRequest as (opts: OpenRequestOpts) => void;
      const liveGetPageProductId = _curGetPageProductId;
      const liveWin = _curWin;
      // Add-to-Quote mode: stage the live variant (+ properties) into
      // the basket instead of opening the form. Falls through to the
      // form path when the ATQ sink wasn't wired (basket disabled) so a
      // misconfigured "add_to_quote" mode still does something useful
      // rather than rendering a dead button.
      if (liveResolved.mode === "add_to_quote" && liveAddToQuote) {
        liveAddToQuote(
          buildAddToQuotePayload({
            form,
            fallbackProductId: liveGetPageProductId ? liveGetPageProductId() : undefined,
            win: liveWin,
            doc,
          }),
        );
        return;
      }
      liveOpenRequest(
        buildOpenRequestOpts({
          form,
          openMode: liveResolved.openMode,
          displayMode: liveResolved.displayMode,
          fallbackProductId: liveGetPageProductId ? liveGetPageProductId() : undefined,
          formId: liveResolved.formId,
          win: liveWin,
          doc,
        }),
      );
    } catch {
      /* a broken openRequest/addToQuote must not surface as an unhandled error */
    }
  });

  const insertedNode = insertButton(doc, form, placement, btn, win);
  _liveButton = btn;
  _liveWrapper = insertedNode !== btn ? insertedNode : null;
  _liveAnchor = placement.anchor;
  _liveFingerprint = computeButtonFingerprint(resolved);
  return { injected: true, reason: "ok", button: btn };
}

// ── Top-level activator ────────────────────────────────────────────

export interface AutoPlaceDeps {
  doc?: DocumentLike;
  win?: any;
  /** Override the openRequest sink (defaults to window.QuoteApp). */
  openRequest?: (opts: OpenRequestOpts) => void;
  /** Basket-add sink for `mode: "add_to_quote"`. Wired by index.ts to
   *  enrich the payload + call basket.add. Absent → ATQ mode degrades
   *  to the form path (see performInject). */
  addToQuote?: (payload: AddToQuotePayload) => void;
  /** Override the page-product-id resolver. */
  getPageProductId?: () => string | undefined;
  /** Override the MutationObserver constructor (tests inject a fake). */
  MutationObserverCtor?: any;
}

let _productBtnObserver: any = null;
let _reassertScheduled = false;
/** Cancels whichever scheduler (rAF or setTimeout) is currently
 *  pending, captured as a closure at schedule time so teardown doesn't
 *  need to know which one was used (C4). */
let _reassertCancel: (() => void) | null = null;
/** True whenever auto-place is enabled and "on". The scheduled
 *  reassert `run()` re-checks this before doing anything (C4) — a
 *  second, redundant safety net alongside `_reassertCancel`: even if a
 *  host's `cancelAnimationFrame` silently no-ops, a frame that fires
 *  after teardown must still no-op. Toggle-off is final, never a
 *  resurrect. */
let _active = false;
/** C1: total re-asserts actually performed this page load (NOT reset
 *  by teardown — only `__resetAutoPlaceForTests`, mirroring "resets
 *  only on full re-boot / new page load"). */
let _reassertCount = 0;
/** True once the C1 budget has been hit — latches so the warning fires
 *  exactly once and every later mutation is a cheap no-op. */
let _reassertBudgetHit = false;
const REASSERT_BUDGET = 30;

/** OUR tracked live button node — the C2/C3/E3 source of truth for
 *  "does an auto-place button exist", replacing a bare DOM query. Null
 *  when nothing is currently injected (never injected, or torn down). */
let _liveButton: any = null;
/** The config fingerprint (computeButtonFingerprint) `_liveButton` was
 *  last styled/labelled from — a mismatch on the next call means
 *  reconcile, not no-op. */
let _liveFingerprint: string | null = null;
/** The anchor `_liveButton` was placed/styled relative to at creation
 *  time. Reused as-is on reconcile — placement is NOT re-evaluated
 *  (Lane 3's concern). */
let _liveAnchor: any = null;
/** The layout-neutral wrapper div (Lane 3 / F1) `_liveButton` was
 *  inserted inside, or null when it was inserted bare (plain block
 *  parent — the common case). Tracked the same way `_liveButton` is so
 *  ghost-sweep/teardown remove the wrapper together with the button. */
let _liveWrapper: any = null;

/** The most recent call's resolved config/sinks — the C2 fix. The
 *  click handler and the reassert observer read these AT FIRE TIME
 *  instead of closing over whatever call created them, so the observer
 *  can live forever without ever going stale. Refreshed unconditionally
 *  at the top of every `performInject` call. */
let _curResolved: ResolvedProductButton | null = null;
let _curOpenRequest: ((opts: OpenRequestOpts) => void) | null = null;
let _curGetPageProductId: (() => string | undefined) | undefined;
let _curWin: any = null;
let _curAddToQuote: ((payload: AddToQuotePayload) => void) | undefined;

/** True when a mutation batch is fully explained by OUR OWN insertion
 *  of `ourNode` — i.e. every record is a childList add with `ourNode`
 *  as its only added node, and NO record removes anything. Used to
 *  suppress scheduling a reassert for a batch our own performInject
 *  call just caused (C1) — it would just find "already reconciled"
 *  and waste budget. Deliberately asymmetric: ANY removal in the batch
 *  bypasses suppression unconditionally, because a co-tenant removing
 *  OUR node (the exact case the reassert exists to catch) produces a
 *  mutation record that also names `ourNode` — by identity alone there
 *  is no way to tell "we just added this" from "something just removed
 *  this", so only pure, removal-free, self-only ADDITIONS are ever
 *  treated as self-triggered. An empty/missing `mutations` argument
 *  (e.g. a test double or a hostile MutationObserver polyfill calling
 *  the callback with no records) is never treated as self-triggered —
 *  ambiguous input must not silently swallow a legitimate reassert. */
function isSelfTriggeredMutations(mutations: any, ourNode: any): boolean {
  if (!ourNode || !mutations || typeof mutations.length !== "number" || mutations.length === 0) {
    return false;
  }
  try {
    for (let i = 0; i < mutations.length; i++) {
      const m = mutations[i];
      const added = m && m.addedNodes ? Array.from(m.addedNodes as any) : [];
      const removed = m && m.removedNodes ? Array.from(m.removedNodes as any) : [];
      if (removed.length > 0) return false;
      if (added.length === 0) return false;
      for (const n of added) {
        if (n !== ourNode) return false;
      }
    }
    return true;
  } catch {
    return false;
  }
}

function resolvePageProductId(win: any): string | undefined {
  try {
    const id = win && win.ShopifyAnalytics && win.ShopifyAnalytics.meta
      && win.ShopifyAnalytics.meta.product && win.ShopifyAnalytics.meta.product.id;
    if (id != null && String(id).trim()) return String(id).trim();
  } catch {
    /* ignore */
  }
  return undefined;
}

/** Remove every node carrying `attr` (real + ghost alike). Shared body
 *  for removeAutoPlaceButtons' two identity attributes. */
function removeAllByAttr(doc: any, attr: string): void {
  try {
    const nodes = typeof doc.querySelectorAll === "function" ? doc.querySelectorAll("[" + attr + "]") : null;
    if (nodes && typeof nodes.forEach === "function") {
      nodes.forEach((n: any) => removeNode(n));
      return;
    }
    // Fallback single-node removal.
    const one = doc.querySelector ? doc.querySelector("[" + attr + "]") : null;
    if (one) removeNode(one);
  } catch {
    /* ignore */
  }
}

/** Teardown removal: both the button identity attribute AND the
 *  layout-wrapper identity attribute (Lane 3 / F1) — a wrapper must
 *  never survive its button being torn down. */
function removeAutoPlaceButtons(doc: any): void {
  removeAllByAttr(doc, AUTO_PLACE_BTN_ATTR);
  removeAllByAttr(doc, AUTO_PLACE_WRAP_ATTR);
}

/** Toggle-off must be FINAL (C4) — cancel any scheduled reassert
 *  (belt), disconnect the observer, and drop `_active` (suspenders: the
 *  scheduled `run()` re-checks this even if the cancel above silently
 *  no-op'd on a host with no `cancelAnimationFrame`). Also drops the
 *  live-button tracking refs so a later re-enable starts clean rather
 *  than reconciling against a node that's about to be/was just ripped
 *  out by `removeAutoPlaceButtons`. Deliberately does NOT reset the C1
 *  budget counters — the budget resets only on a full re-boot (new
 *  page load, see `__resetAutoPlaceForTests`), not on a mid-page
 *  toggle, so a flapping enable/disable can't be used to keep
 *  refilling it. `doc`, when supplied, also removes the cascade-
 *  authority style sheet (Lane 3 / B2) — optional because
 *  `__resetAutoPlaceForTests` may run with no ambient document at all
 *  (node-only test environment). */
function teardownObserver(doc?: any): void {
  try {
    _productBtnObserver && _productBtnObserver.disconnect && _productBtnObserver.disconnect();
  } catch {
    /* ignore */
  }
  _productBtnObserver = null;
  _reassertScheduled = false;
  _active = false;
  if (_reassertCancel) {
    try {
      _reassertCancel();
    } catch {
      /* ignore */
    }
    _reassertCancel = null;
  }
  _liveButton = null;
  _liveFingerprint = null;
  _liveAnchor = null;
  _liveWrapper = null;
  if (doc) removeAutoPlaceStyleTag(doc);
}

function setupReassertObserver(doc: DocumentLike, MOCtor: any, win: any): void {
  if (_productBtnObserver) return; // single shared observer, lives forever — no staleness (C2)
  const Ctor = MOCtor
    || (win && win.MutationObserver)
    || (typeof MutationObserver !== "undefined" ? MutationObserver : null);
  if (!Ctor) return;
  const target = (doc as any).body || (doc as any).documentElement;
  if (!target || typeof target.appendChild !== "function") return;
  try {
    _productBtnObserver = new Ctor((mutations: any) => {
      // C1: once the budget's blown, stop even SCHEDULING further
      // reasserts (not just running them) — a co-tenant DOM war must
      // not keep waking this observer up forever.
      if (_reassertBudgetHit) return;
      // C1 self-trigger suppression: our own insert showing up as a
      // mutation must not schedule a reassert against itself.
      if (isSelfTriggeredMutations(mutations, _liveButton)) return;
      // Debounce a burst of mutations (Swym injects many nodes at
      // once) into a single re-assert. performInject is idempotent,
      // so a spurious extra run is harmless.
      if (_reassertScheduled) return;
      _reassertScheduled = true;
      const run = () => {
        _reassertScheduled = false;
        _reassertCancel = null;
        // C4: a frame that fires after teardown must no-op even if
        // the cancel attempt above silently failed.
        if (!_active) return;
        _reassertCount++;
        if (_reassertCount > REASSERT_BUDGET) {
          if (!_reassertBudgetHit) {
            _reassertBudgetHit = true;
            try {
              console.warn("[qs] auto-place: giving up re-asserting (co-tenant conflict)");
            } catch {
              /* ignore */
            }
          }
          return;
        }
        try {
          // C2: use the LATEST refs, not whatever call first created
          // this observer — non-null because setupReassertObserver is
          // only ever called immediately after a performInject call,
          // which refreshes these unconditionally.
          performInject(
            doc,
            _curResolved as ResolvedProductButton,
            _curOpenRequest as (opts: OpenRequestOpts) => void,
            _curGetPageProductId,
            _curWin,
            _curAddToQuote,
          );
        } catch {
          /* never throw from an observer callback */
        }
      };
      if (win && typeof win.requestAnimationFrame === "function") {
        const id = win.requestAnimationFrame(run);
        _reassertCancel = () => {
          try {
            win.cancelAnimationFrame && win.cancelAnimationFrame(id);
          } catch {
            /* ignore */
          }
        };
      } else if (typeof setTimeout !== "undefined") {
        const id = setTimeout(run, 0);
        _reassertCancel = () => {
          try {
            clearTimeout(id);
          } catch {
            /* ignore */
          }
        };
      } else {
        run();
      }
    });
    _productBtnObserver.observe(target, { childList: true, subtree: true });
  } catch {
    /* observer setup failed — the one-shot inject above still stands */
  }
}

/** Entry point called from boot() (cache phase + network phase). Gated
 *  on auto_place_enabled; never throws. Safe to call repeatedly — the
 *  inject is idempotent and the observer is a singleton. When config
 *  flips OFF (e.g. the network phase overrides a stale enabled cache)
 *  it tears down any injected button + the observer. */
export function autoPlaceProductButton(
  cfg: { product_button?: ProductButtonConfig; accent_color?: string | null } | null | undefined,
  deps: AutoPlaceDeps = {},
): void {
  try {
    const doc: DocumentLike | undefined =
      deps.doc || (typeof document !== "undefined" ? (document as any) : undefined);
    if (!doc) return;
    const win = deps.win || (typeof window !== "undefined" ? (window as any) : undefined);

    const resolved = resolveProductButtonConfig(cfg?.product_button, cfg?.accent_color);
    if (!resolved.enabled) {
      // Off — tear down anything we placed + stop re-asserting so the
      // toggle-off path removes the button (on next boot, and live when
      // the two-phase fetch disagrees with a stale enabled cache).
      //
      // BUT only when we have a REAL config. fetchEmbedShopConfigWithVersion
      // returns a `{ config: {} }` stub on a server error / non-ok
      // response (resolved, not rejected) — an empty object here means
      // "no signal", NOT "merchant disabled it". Removing on that stub
      // would rip out a button the cache correctly injected whenever the
      // network hiccups. A genuine merchant config always carries many
      // keys (brand_name, accent_color, …), so a non-empty object is a
      // safe proxy for "authoritative".
      const isRealConfig = !!cfg && typeof cfg === "object" && Object.keys(cfg).length > 0;
      if (isRealConfig) {
        removeAutoPlaceButtons(doc);
        teardownObserver(doc);
      }
      return;
    }

    const openRequest =
      deps.openRequest ||
      ((o: OpenRequestOpts) => {
        try {
          win && win.QuoteApp && win.QuoteApp.openRequest && win.QuoteApp.openRequest(o);
        } catch {
          /* no-op — global not ready yet; the re-assert will retry */
        }
      });
    const getPageProductId = deps.getPageProductId || (() => resolvePageProductId(win));
    const addToQuote = deps.addToQuote;

    // C4: "on" from here — the scheduled reassert's _active check
    // gates on this, not on whether an observer happens to exist yet.
    _active = true;
    performInject(doc, resolved, openRequest, getPageProductId, win, addToQuote);
    setupReassertObserver(doc, deps.MutationObserverCtor, win);
  } catch {
    /* never throw — a broken theme must not break the rest of embed.js */
  }
}

/** Test-only reset of the module-level observer + lifecycle state so
 *  each test starts from a clean slate — the moral equivalent of a new
 *  page load, which is also the only thing that resets the C1 budget
 *  counters (see teardownObserver's note). No-op cost in production. */
export function __resetAutoPlaceForTests(): void {
  const doc = typeof document !== "undefined" ? (document as any) : undefined;
  teardownObserver(doc);
  _reassertCount = 0;
  _reassertBudgetHit = false;
  _curResolved = null;
  _curOpenRequest = null;
  _curGetPageProductId = undefined;
  _curWin = null;
  _curAddToQuote = undefined;
}

// ── Collection-card Add-to-Quote (Door 2) ──────────────────────────
//
// On collection / search grids the Hide-ATC catalog rule flags each
// matched card with data-qs-hide-atc-target="1" (see catalog-rules.ts)
// and its CSS hides that card's Add-to-Cart. This is the "soft
// replace": where ATC was hidden, we drop an Add-to-Quote control that
// feeds the SAME basket sink the product-page button uses (index.ts's
// handleAddToQuote). Pure resolution + a per-card idempotent injector
// live here (node-only testable, mirrors performInject); index.ts owns
// the scan + re-assert observer + the basket.enabled gate.
//
// A collection card rarely exposes a *selected* variant (the shopper
// hasn't opened the product), so we resolve whatever stable id the card
// carries and let the sink resolve the first/only variant server-side
// (the /api/public/product `type=product` path). Cards that expose
// neither a variant nor a product id are SKIPPED — no half-wired button.

export const CARD_ATQ_BTN_CLASS = "qs-card-atq-btn";
export const CARD_ATQ_BTN_ATTR = "data-qs-card-atq";

export interface CardAtqTarget {
  variantId?: string;
  productId?: string;
}

function readElAttr(node: any, name: string): string {
  try {
    if (node && typeof node.getAttribute === "function") {
      const v = node.getAttribute(name);
      if (v != null && String(v).trim()) return String(v).trim();
    }
  } catch {
    /* ignore */
  }
  return "";
}

function readCardValue(card: any, sel: string): string {
  try {
    const node = card.querySelector ? card.querySelector(sel) : null;
    if (node && node.value != null && String(node.value).trim()) return String(node.value).trim();
  } catch {
    /* ignore */
  }
  return "";
}

/** A single-variant quick-add form on the card carries the variant id
 *  in its [name="id"] input/select — read it in two steps (form, then
 *  the id control) so no descendant-combinator selector is needed. */
function readCardFormVariant(card: any): string {
  try {
    const form = card.querySelector ? card.querySelector(PRODUCT_FORM_SELECTOR) : null;
    if (form && typeof form.querySelector === "function") {
      const idEl = form.querySelector(VARIANT_SELECTOR);
      if (idEl && idEl.value != null && String(idEl.value).trim()) return String(idEl.value).trim();
    }
  } catch {
    /* ignore */
  }
  return "";
}

/** Resolve a collection card's product/variant identity for ATQ.
 *  Precedence — an explicit variant id first (a card /cart/add form's
 *  [name="id"], or data-variant-id), else a product id (data-product-id
 *  / [name="product-id"]) which the sink turns into the first/only
 *  variant. Returns null when the card carries neither — the caller
 *  SKIPS it. Never throws. */
export function resolveCardAtqTarget(card: any): CardAtqTarget | null {
  if (!card) return null;
  const variantId =
    readElAttr(card, "data-variant-id") ||
    readElAttr(card, "data-product-variant-id") ||
    readCardFormVariant(card) ||
    readCardValue(card, VARIANT_SELECTOR);
  let productId =
    readElAttr(card, "data-product-id") ||
    readCardValue(card, PRODUCT_ID_SELECTOR);
  if (!productId && card.querySelector) {
    try { productId = readElAttr(card.querySelector("[data-product-id]"), "data-product-id"); } catch { /* ignore */ }
  }
  if (!variantId && !productId) return null;
  const out: CardAtqTarget = {};
  if (variantId) out.variantId = variantId;
  if (productId) out.productId = productId;
  return out;
}

/** True when the card already carries an injected ATQ button. Per-card
 *  scope (unlike the product-page button's document-wide guard) — one
 *  button per card, re-asserted idempotently by index.ts's observer. */
export function hasCardAtqButton(card: any): boolean {
  try {
    return !!(card && typeof card.querySelector === "function" && card.querySelector("." + CARD_ATQ_BTN_CLASS));
  } catch {
    return false;
  }
}

/** Style the injected card button — a simple full-width filled pill.
 *  Deliberately NOT the anchor-copy machinery of the product-page
 *  button (a card has no add-to-cart anchor whose exact size/shape we
 *  want to mirror); a clean, predictable control reads better across
 *  the wildly varying card layouts. */
function applyCardButtonStyle(btn: any, bgColor: string, textColor: string): void {
  const s = btn && btn.style;
  if (!s) return;
  s.display = "block";
  s.width = "100%";
  s.margin = "8px 0 0";
  s.padding = "9px 14px";
  s.background = bgColor;
  s.backgroundColor = bgColor;
  s.color = textColor;
  s.border = "none";
  s.borderRadius = "8px";
  s.cursor = "pointer";
  s.textAlign = "center";
  s.fontSize = "13px";
  s.fontWeight = "600";
  s.lineHeight = "1.2";
  s.fontFamily = "inherit";
  s.appearance = "none";
  s.webkitAppearance = "none";
}

/** Inject an Add-to-Quote button into ONE collection card. Idempotent
 *  (per-card guard) and graceful — skips a card with no resolvable
 *  id (returns reason "skipped") and never throws. The click resolves
 *  the card's id at BIND time (a card's id doesn't change under the
 *  shopper the way a product page's live-selected variant does) and
 *  hands it to the basket sink. */
export function injectCardAddToQuote(opts: {
  card: any;
  doc: DocumentLike;
  label: string;
  bgColor: string;
  textColor: string;
  addToQuote: (payload: AddToQuotePayload) => void;
}): InjectResult {
  const { card, doc, label, bgColor, textColor, addToQuote } = opts;
  if (!card || !doc) return { injected: false, reason: "skipped" };
  if (hasCardAtqButton(card)) return { injected: false, reason: "exists" };
  const target = resolveCardAtqTarget(card);
  if (!target) return { injected: false, reason: "skipped" };

  const btn = doc.createElement("button");
  // type="button" — the card may host a quick-add <form>; the default
  // type="submit" would POST it (add to cart) on click.
  btn.setAttribute("type", "button");
  btn.className = CARD_ATQ_BTN_CLASS;
  btn.setAttribute(CARD_ATQ_BTN_ATTR, "1");
  btn.textContent = label;
  applyCardButtonStyle(btn, bgColor, textColor);

  btn.addEventListener("click", (ev?: any) => {
    try {
      if (ev && typeof ev.preventDefault === "function") ev.preventDefault();
    } catch {
      /* ignore */
    }
    try {
      const payload: AddToQuotePayload = {};
      if (target.variantId) payload.variantId = target.variantId;
      if (target.productId) payload.productId = target.productId;
      addToQuote(payload);
    } catch {
      /* a broken sink must not surface as an unhandled error */
    }
  });

  try {
    card.appendChild(btn);
  } catch {
    return { injected: false, reason: "skipped" };
  }
  return { injected: true, reason: "ok", button: btn };
}
